import { useState, useEffect, useRef } from 'react';
import { Form, Input, Select, Button, Space, message, Tag, Tabs, InputNumber, DatePicker } from 'antd';
import { UserAddOutlined, SearchOutlined, ReloadOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { getChannelLogo } from '../utils/channelLogos';
import { testApi, metaApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const { TextArea } = Input;
const CHANNELS = ['PYVIO', 'TAZAPAY', 'MDAQ', 'TRANSFI', 'MOOLAHGO', 'AVENIA', 'CORPAY', 'GOWD'];

/* ── Avenia KYB 枚举（与后端 AveniaKybEnums / AveniaUboControlRoleEnum / AveniaDocumentTypeEnum 对齐） ── */
const AVENIA_REASON_FOR_ACCOUNT_OPENING = [
  'charitable_donations', 'ecommerce_retail_payments', 'investment_purposes', 'other',
  'payments_to_friends_or_family_abroad', 'payroll', 'personal_or_living_expenses', 'protect_wealth',
  'purchase_goods_and_services', 'receive_payments_for_goods_and_services', 'tax_optimization',
  'third_party_money_transmission', 'treasury_management',
];
const AVENIA_SOURCE_OF_FUNDS = [
  'business_loans', 'grants', 'inter_company_funds', 'investment_proceeds', 'legal_settlement',
  'owners_capital', 'pension_retirement', 'sale_of_assets', 'sales_of_goods_and_services',
  'third_party_funds', 'treasury_reserves',
];
const AVENIA_NUMBER_OF_EMPLOYEES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001+'];
const AVENIA_ANNUAL_REVENUE_USD = ['less_than_100k', '100k_to_1m', '1m_to_10m', '10m_to_50m', '50m_to_100m', 'more_than_100m'];
const AVENIA_UBO_CONTROL_ROLES = [
  'CEO', 'CFO', 'COO', 'CTO', 'President', 'Vice President', 'Director', 'Managing Director',
  'Board Member', 'Chairman', 'Managing Partner', 'General Partner', 'Partner',
  'Authorized Signatory', 'Secretary', 'Treasurer', 'Owner', 'Founder', 'Manager', 'Member',
  'General Counsel', 'Comptroller', 'Chief Compliance Officer',
];
// 企业类文档只能单面；仅身份证件类允许 isDoubleSided=true；活体自拍固定单面
const AVENIA_COMPANY_DOC_TYPES = ['CERTIFICATE-OF-INCORPORATION', 'COMPANY-TAX-IDENTIFICATION-DOCUMENT'];
const AVENIA_IDENTITY_DOC_TYPES = ['ID', 'DRIVERS-LICENSE', 'PASSPORT', 'RESIDENCE-PERMIT'];
const AVENIA_UBO_DOC_TYPES = [...AVENIA_IDENTITY_DOC_TYPES, 'SELFIE-FROM-LIVENESS'];

const TAZAPAY_COMPANY_TYPES = ['sole_proprietorship', 'company', 'non_profit', 'government_entity'];

const TAZAPAY_INDUSTRY_OPTIONS = [
  { label: '📱 Digital Products', options: [
    'Digital Products - Apps, eBooks, Blogs & Games','Digital Products - Digital Merchandise','Digital Products - Music, Video, Channels & Podcast',
    'Digital Products - Online Gaming (based on skill)','Digital Products - Other Digital Goods','Digital Products - Large Digital Goods Merchant (>25 Mn Txns annually)',
  ].map(v => ({ label: v, value: v }))},
  { label: '🛒 Ecommerce', options: [
    'Ecommerce - Consumer Electronics','Ecommerce - Consumer Electronics (Mobiles)','Ecommerce - Beauty Products & Cosmetics',
    'Ecommerce - General Merchandise','Ecommerce - Home Appliances','Ecommerce - Dropshipping','Ecommerce - Diversified/Others',
    'Ecommerce - Men and boys clothing','Ecommerce - Women fashion','Ecommerce - Children clothing','Ecommerce - Sports clothing',
    'Ecommerce - General clothing stores (for men, women, children)','Ecommerce - General clothing stores (for men and women)',
    'Ecommerce - Footwear including athletic footwear','Ecommerce - Sports & Travel Goods','Ecommerce - Books Stores',
    'Ecommerce - Food & Non-Alcohol Drinks (Grocery and Supermarkets)','Ecommerce - Food & Non-Alcohol Drinks (Bakeries)',
    'Ecommerce - Food & Non-Alcohol Drinks (Confectionery / Candy)','Ecommerce - Food & Non-Alcohol Drinks (Dairy products)',
    'Ecommerce - Food & Non-Alcohol Drinks (Miscellaneous / Specialty stores)','Ecommerce - Pet Food',
    'Ecommerce - Meat & Poultry','Ecommerce - Supplements or Nutraceuticals',
    'Ecommerce - Home interior fixtures and decorating items','Ecommerce - Home, Decor & Furniture-Warehouse Stores',
    'Ecommerce - Finished Furniture and accessories','Ecommerce - Hobby, Toy, and Game Shops',
    'Ecommerce - Artwork / Art Dealers and Galleries','Ecommerce - Art & Craft Materials, Handicraft and tools',
    'Ecommerce - Aroma, Organic & Herbal','Ecommerce - Packaging, Office & Stationery',
    'Ecommerce - Electronic Hardware, Tools & Parts','Ecommerce - Hardware stores',
    'Ecommerce - Electrical and plumbing supplies, tools and power tools',
    'Ecommerce - Building & Construction Materials (General)','Ecommerce - Building & Construction Materials (Glass, Paint and Wallpaper)',
    'Ecommerce - Building & Construction Materials (Floor Covering)',
    'Ecommerce - Automobiles - Automobile sale/lease and repair, replacement of parts',
    'Ecommerce - Automobiles - Automobile parts/tools, accessories and equipment',
    'Ecommerce - Automobiles - Automobile tyre sale, installation and repair',
    'Ecommerce - Automobiles - Motorcycle Shops and Dealers','Ecommerce - Automobiles - Bicycles, bicycle parts, accessories and repair',
    'Ecommerce - Automobiles - Car and Truck Dealers (Used Only) Sales, Service, Repairs, Parts, and Leasing',
    'Ecommerce - Automobiles (B2B) - Motor Vehicle Supplies and New Parts',
    'Ecommerce - Camera and Photographic Supply Stores','Ecommerce - Clothing rental','Ecommerce - Custom made clothing (to order)',
    'Ecommerce - Discount Stores','Ecommerce - Drapery, Window Covering, and Upholstery Stores','Ecommerce - Duty Free Stores',
    'Ecommerce - Equipment, Tool, Furniture, and Appliance Rental and Leasing',
    'Ecommerce - Fur jackets, coats','Ecommerce - Gift, Card, Novelty and Souvenir Shops',
    'Ecommerce - Glassware/Crystal Stores','Ecommerce - Luggage and storage (incl backpacks, briefcases, purses and wallets)',
    'Ecommerce - Miscellaneous Clothing (e.g., Swimwear)','Ecommerce - Music Stores',
    'Ecommerce - AgriProducts - Garden Products and Fertilizers','Ecommerce - AgriProducts - Florists and Novelty Products',
    'Ecommerce - Religious Goods Stores','Ecommerce - Sewing, Needlework, Fabric and Piece Goods Stores',
    'Ecommerce - Stamp and Coin Stores','Ecommerce - Used Merchandise and Secondhand Stores',
    'Ecommerce - Wigs, Toupes, hair pieces and extensions','Ecommerce - Women accessories and specialty shops',
    'Ecommerce (B2B) - Building & Construction Materials (General)',
    'Ecommerce (B2B) - Uniform supply and Commercial / Industrial clothing',
    'Ecommerce (B2B) - Clocks, watches, silverware gems and jewelry','Ecommerce (B2B) - Commercial footwear',
    'Ecommerce (B2B / Others) - Medical, dental, ophthalmic and hospital equipment and supplies & PPE',
    'Ecommerce (B2C) - Clocks, watches, silverware gems and jewelry',
  ].map(v => ({ label: v, value: v }))},
  { label: '🎓 Education', options: [
    'Education - Elementary and Secondary Schools','Education - Elearning, Online Tutoring, Homework & Test Prep',
    'Education - Colleges, Universities, Professional Schools, and Junior Colleges',
    'Education - Online Education and Correspondence Schools',
    'Education (others) - Other Educational Services not elsewhere classified',
    'Education (others) - Business and Secretarial Schools','Education (others) - Vocational and Trade Schools',
  ].map(v => ({ label: v, value: v }))},
  { label: '💰 Financial Service', options: [
    'Financial Service - Digital Wallets, PSPs & Fintech','Financial Service - Banks and NBFI',
    'Financial Service - Brokerages & Exchanges','Financial Service - Remittance & MSB',
    'Financial Service - Life and Non-Life Insurance policies','Financial Service - Investment Banking & Financial Advisory',
    'Financial Service - Fund, Wealth Management & Investment Holding','Financial Service - CFD and Forex',
    'Financial Service - Carbon Credit','Financial Service - Crowdfunding platform (equity)',
    'Financial Service - Crowdfunding platforms (debt)','Financial Service - Prop Trading Simulation',
    'Financial Service - Other Financial Services (Automated Cash disbursements)',
    'Financial Service - Other Financial Services (Manual Cash disbursements)',
    'Financial Service - Other Financial Services (Non-classified)','Financial Services - Intra-Company Purchases',
  ].map(v => ({ label: v, value: v }))},
  { label: '🏭 General Industries', options: [
    'General Industries - Agri, Forestry & Fishing',
    'General Industries - Trade & Wholesale of General Goods (Commercial Equipment)',
    'General Industries - Trade & Wholesale of General Goods (Chemicals and Allied Products)',
    'General Industries - Trade & Wholesale of General Goods (Computers Hardware, Software)',
    'General Industries - Trade & Wholesale of General Goods (Durable Goods)',
    'General Industries - Trade & Wholesale of General Goods (Flower)',
    'General Industries - Trade & Wholesale of General Goods (Hardware, Equipment and Supplies)',
    'General Industries - Trade & Wholesale of General Goods (Industrial Supplies)',
    'General Industries - Trade & Wholesale of General Goods (Semi-finished metal products)',
    'General Industries - Trade & Wholesale of General Goods (Non-durable goods)',
    'General Industries - Trade & Wholesale of General Goods (Office and Commercial Furniture)',
    'General Industries - Trade & Wholesale of General Goods (Paints and related equipment)',
    'General Industries - Trade & Wholesale of General Goods (Photographic equipment, etc)',
    'General Industries - Trade & Wholesale of General Goods (Piece Goods, Notions, and Other Dry Goods)',
    'General Industries - Trade & Wholesale of General Goods (Commercial machines and equipment)',
    'General Industries - Trade & Wholesale of General Goods (Stationery, Office Supplies)',
    'General Industries - Trade & Wholesale of General Goods (Typewriter Stores)',
    'General Industries - Trade & Wholesale of General Goods (Electrical Parts and equipment)',
  ].map(v => ({ label: v, value: v }))},
  { label: '🔧 General Service', options: [
    'General Service - Other General Services','General Service - Computer Network/Information Services',
    'General Service - Health and Beauty Spas','General Service - Photography Studios',
    'General Service - Recreation Services','General Service - Recycling & Waste Management',
    'General Service - Sporting and Recreational Camps','General Service - Utility',
    'General Service - Miscellaneous Personal Services',
    'General Service - Membership Organizations (Civic, Social, and Fraternal)',
    'General Service - Membership Organizations (Non-sports)',
    'General Service - Membership Organizations (Professional Sports Clubs)',
    'General Service - Membership Organizations (Recreation Sports Clubs)',
    'General Service - Membership Organizations (Automobile Associations)',
    'General Services - Cleaning, Maintenance, and Janitorial Services',
    'General Services - Exterminating and Disinfecting Services',
    'General Services - Laundries Family and Commercial','General Services - Laundry, Cleaning, and Garment Services',
    'General Services - Testing Laboratories (Non-Medical Testing)','General Services - Dry Cleaners',
    'General Services - Buying and Shopping Services and Clubs','General Services - Carpet and Upholstery Cleaning',
  ].map(v => ({ label: v, value: v }))},
  { label: '🏥 Medical Services', options: [
    'Medical Services - Hospitals','Medical Services - Telemedicine & Telehealth',
    'Medical Services - Doctors and Physicians','Medical Services - Dentists and Orthodontists',
    'Medical Services - Optometrists and Ophthalmologists','Medical Services - Chiropractors',
    'Medical Services - Opticians, Optical Goods, and Eyeglasses','Medical Services - Osteopaths',
    'Medical Services - Podiatrists and Chiropodists','Medical Services - Veterinary Services',
    'Medical Services - Medical and Dental Laboratories','Medical Services - Nursing and Personal Care Facilities',
  ].map(v => ({ label: v, value: v }))},
  { label: '📦 Others', options: [
    'Others - Cryptocurrency','Others - Funding, Seed Capital Investment, and Venture Capital',
    'Others - Books, Periodicals and Newspapers','Others - Boat Dealers',
    'Others - Camper, Recreational and Utility Trailer Dealers','Others - Public Golf Courses',
    'Others - Parking Lots, Parking Meters and Garages','Others - Motor Homes Dealers',
    'Others - Motor Home and Recreational Vehicle Rentals','Others - Motion Picture and Video Tape Production and Distribution',
    'Others - Mobile Home Dealers','Others - Miscellaneous Automotive, Aircraft, and Farm Equipment Dealers',
    'Others - Information Retrieval Services','Others - Electric Vehicle Charging','Others - Snowmobile Dealers',
    'Others - Computer Maintenance, Repair and Services','Others - Trailer Parks and Campgrounds',
    'Others - Automated Fuel Dispensers','Others - Truck and Utility Trailer Rentals',
    'Others - Video Amusement Game Supplies','Others - Public Warehousing and Storage',
    'Others - Swimming Pools Sales and Service','Others - Telegraph Services','Others - Tent and Awning Shops',
    'Others - Fireplace, Fireplace Screens and Accessories Stores','Others - Package Tour Operators',
  ].map(v => ({ label: v, value: v }))},
  { label: '🏪 Other Offline', options: [
    'Other Offline - Eating Places and Restaurants','Other Offline - Fast Food Restaurants','Other Offline - Caterers',
    'Other Offline - Drinking Places (Alcoholic Beverages)','Other Offline - Beauty and Barber Shops',
    'Other Offline - Car Washes','Other Offline - Advertising Services',
    'Other Offline - Automotive Service Shops (Non-Dealer)','Other Offline - Automotive Body Repair Shops',
    'Other Offline - Automotive Paint Shops','Other Offline - Air Conditioning and Refrigeration Repair Shops',
    'Other Offline - Bowling Alleys','Other Offline - Billiard and Pool Establishments',
    'Other Offline - Bands, Orchestras, and Miscellaneous Entertainers',
    'Other Offline - Electrical Contractors','Other Offline - Electronics Repair Shops',
    'Other Offline - Electrical and Small Appliance Repair Shops','Other Offline - Carpentry Contractors',
    'Other Offline - Concrete Work Contractors','Other Offline - Dance Halls, Studios and Schools',
    'Other Offline - Door To Door Sales','Other Offline - DVD/Video Tape Rental Stores',
    'Other Offline - Emergency Services (GCAS)','Other Offline - Funeral Services and Crematories',
    'Other Offline - Furniture Reupholstery, Repair, and Refinishing',
    'Other Offline - Heating, Plumbing, and Air Conditioning Contractors',
    'Other Offline - Masonry, Stonework, Tile Setting, Plastering and Insulation Contractors',
    'Other Offline - Miscellaneous Repair Shops and Related Services','Other Offline - Motion Picture Theaters',
    'Other Offline - News Dealers and Newsstands','Other Offline - Record Stores',
    'Other Offline - Roofing, Siding, and Sheet Metal Work Contractors',
    'Other Offline - Service Stations','Other Offline - Shoe Repair Shops, Shoe Shine Parlors, and Hat Cleaning Shops',
    'Other Offline - Special Trade Contractors','Other Offline - Specialty Cleaning, Polishing and Sanitation Preparations',
    'Other Offline - Tire Retreading and Repair Shops','Other Offline - Tolls and Bridge Fees',
    'Other Offline - Towing Services','Other Offline - Video Game Arcades/Establishments',
    'Other Offline - Watch, Clock and Jewelry Repair','Other Offline - Welding Services',
    'Other Offline - Wholesale Clubs','Other Offline - Wrecking and Salvage Yards',
  ].map(v => ({ label: v, value: v }))},
  { label: '🌐 Platforms', options: [
    'Platforms - Goods Marketplace','Platforms - Goods Marketplace (with dedicated brand stores)',
    'Platforms - Services Marketplace','Platforms - Digital Content Marketplace & Appstore',
    'Platforms - Super Apps','Platforms - Social Media','Platforms - Other Platforms',
  ].map(v => ({ label: v, value: v }))},
  { label: '💼 Professional Service', options: [
    'Professional Service - App/Website/Software Development & Maintenance',
    'Professional Service - BPO & KPO','Professional Service - Consulting & Coaching',
    'Professional Service - Content Writing & Proof Reading','Professional Service - Counseling Services',
    'Professional Service - Credit Rating & Financial Data','Professional Service - Customer Survey & Market Research',
    'Professional Service - Design & Architecture (Landscape and Horticultural)',
    'Professional Service - Design & Architecture (Others)','Professional Service - Freelance (Graphic Design)',
    'Professional Service - Freelance (Others)','Professional Service - Real Estate Sales & Brokerage',
    'Professional Service - Printing, publishing and book binding',
    'Professional Service - Film development, photographic prints',
    'Professional Service - Photocopying, Reproduction, and Blueprinting Services',
    'Professional Service - Typesetting, Plate Making and Related Services',
    'Professional Services - Accounting, Auditing, Payroll, Bookkeeping Services',
    'Professional Services - Architectural, Engineering, and Surveying Services',
    'Professional Services - Legal Advices and Services','Professional Services - Tax Preparation Services',
    'Professional Services - Employment Agencies and Temporary Help Services',
    'Professional Services - Detective Agencies, Protective Services, and Security Services',
    'Professional Services - Airports, Flying Fields, and Airport Terminals',
    'Professional Services - Motor Freight Carriers and Trucking',
    'Professional Services - General Contractors','Professional Services - Courier Services',
    'Professional Services (Govt) - Court Costs','Professional Services (Govt) - Fines',
    'Professional Services (Govt) - Not elsewhere classified','Professional Services (Govt) - Postal',
    'Professional Services (Govt) - Tax Payments',
    'Professional Services (Govt) - U.S. Federal Government Agencies or Departments',
    'Professional Services (Govt) - Bail and Bond Payments',
  ].map(v => ({ label: v, value: v }))},
  { label: '☁️ Software as a Service', options: [
    'Software as a Service - Enterprise Software','Software as a Service - Education Technology (EdTech)',
    'Software as a Service - Employer on Record','Software as a Service - Benefits, Rewards & Loyalty',
    'Software as a Service - Other Software as a Service',
  ].map(v => ({ label: v, value: v }))},
  { label: '📡 Telecommunication', options: [
    'Telecommunication Services - Telecommunication Services',
  ].map(v => ({ label: v, value: v }))},
  { label: '✈️ Travel & Leisure', options: [
    'Travel & Leisure - Accommodation (Hotels, etc)','Travel & Leisure - Airlines',
    'Travel & Leisure - B2B Travel','Travel & Leisure - Boat Rentals and Leasing',
    'Travel & Leisure - Cruise Lines','Travel & Leisure - Destination Management Companies',
    'Travel & Leisure - Event Ticketing','Travel & Leisure - Marinas, Marine Service, and Supplies',
    'Travel & Leisure - Travel Agencies & OTAs',
    'Travel & Leisure - Tourist Attractions (Tourist Attractions and Exhibits)',
    'Travel & Leisure - Tourist Attractions (Aquariums, Seaquariums, Dolphinariums, and Zoos)',
    'Travel & Leisure - Tourist Attractions (Amusement Parks, Circuses, Carnivals)',
    'Travel & Leisure - Other Transportation Services (Taxicabs and Limousines)',
    'Travel & Leisure - Other Transportation Services (Railroads)',
    'Travel & Leisure - Other Transportation Services (Ambulance Services)',
    'Travel & Leisure - Other Transportation Services (Automobile Rental Agency)',
    'Travel & Leisure - Other Transportation Services (Bus Lines)',
    'Travel & Leisure - Other Transportation Services (Local and Suburban Commuter Passenger Transportation)',
    'Travel & Leisure - Other Transportation Services (Passenger Railways)',
    'Travel & Leisure - Other Transportation Services',
    'Travel & Leisure - Other Travel & Leisure (Timeshares)',
    'Travel & Leisure - Other Travel & Booking services (e.g., Visa)',
  ].map(v => ({ label: v, value: v }))},
];
const TAZAPAY_LEGAL_ROLES = ['director', 'shareholder', 'beneficial_owner', 'authorised_signatory', 'authorised_representative', 'other'];
// 变更（2026-06）：fileType 直接映射渠道 documents.type，按渠道补全枚举并区分企业/代表人范围。
// 企业文档（11 种）
const TAZAPAY_DOC_TYPES_COMPANY = [
  { value: 'proof_of_identity', label: 'proof_of_identity' },
  { value: 'proof_of_address', label: 'proof_of_address' },
  { value: 'registration_documents', label: 'registration_documents' },
  { value: 'article_of_incorporation', label: 'article_of_incorporation' },
  { value: 'shareholder_registry', label: 'shareholder_registry' },
  { value: 'identity', label: 'identity' },
  { value: 'business', label: 'business' },
  { value: 'other', label: 'other' },
  { value: 'address', label: 'address' },
  { value: 'source_of_wealth', label: 'source_of_wealth' },
  { value: 'person', label: 'person' },
];
// 代表人文档（8 种，不含 business / source_of_wealth / person）
const TAZAPAY_DOC_TYPES_LEGAL = [
  { value: 'proof_of_identity', label: 'proof_of_identity' },
  { value: 'proof_of_address', label: 'proof_of_address' },
  { value: 'registration_documents', label: 'registration_documents' },
  { value: 'article_of_incorporation', label: 'article_of_incorporation' },
  { value: 'shareholder_registry', label: 'shareholder_registry' },
  { value: 'identity', label: 'identity' },
  { value: 'address', label: 'address' },
  { value: 'other', label: 'other' },
];
// 文档子类型（映射渠道 documents.sub_type，渠道必填）：渠道无 residence_permit；同时存在 drivers_license 与 driving_license
const TAZAPAY_DOC_SUBTYPES = [
  { value: 'passport', label: 'passport' },
  { value: 'drivers_license', label: 'drivers_license' },
  { value: 'driving_license', label: 'driving_license' },
  { value: 'national_id', label: 'national_id' },
  { value: 'other', label: 'other' },
];
// 文档标签（映射渠道 documents.tag，渠道必填，区分大小写，且区分企业/代表人）
// 企业文档（7 个）
const TAZAPAY_DOC_TAGS_COMPANY = [
  { value: 'registrationProofDoc', label: 'registrationProofDoc' },
  { value: 'AddressProofDoc', label: 'AddressProofDoc' },
  { value: 'beneficialOwnershipDoc', label: 'beneficialOwnershipDoc' },
  { value: 'bankStatementProofDoc', label: 'bankStatementProofDoc' },
  { value: 'previousCardStatementProofDoc', label: 'previousCardStatementProofDoc' },
  { value: 'sampleInvoicesDoc', label: 'sampleInvoicesDoc' },
  { value: 'additionalDocs', label: 'additionalDocs' },
];
// 代表人文档（4 个）
const TAZAPAY_DOC_TAGS_LEGAL = [
  { value: 'AddressProofDoc', label: 'AddressProofDoc' },
  { value: 'identityProofTypeFrontDoc', label: 'identityProofTypeFrontDoc' },
  { value: 'identityProofTypeBackDoc', label: 'identityProofTypeBackDoc' },
  { value: 'additionalDocs', label: 'additionalDocs' },
];

const MDAQ_BUSINESS_TYPES = [
  'Information Technology', 'Financial Services', 'E-Commerce', 'Manufacturing',
  'Wholesale Trading', 'Professional Services', 'Logistics and Transportation',
  'Food and Drinks', 'Travel Agency', 'Other',
];
const MDAQ_SOURCE_OF_FUNDS = ['first_party_only', 'related_third_party'];
const MDAQ_OWNERSHIP_LAYERS = ['1', '2', '3_or_more', 'unknown'];
const MDAQ_PAYMENT_MODES = ['telegraphic_transfer', 'card', 'cash', 'cheque', 'other'];
const MDAQ_ONBOARDING_MODES = ['face_to_face', 'non_face_to_face'];
const MDAQ_PRODUCT_COMPLEXITY = ['simple', 'medium', 'complex'];
const MDAQ_ENTITY_TYPES = ['private_company', 'public_company', 'partnership', 'sole_proprietorship', 'trust', 'other'];

/* ── GOWD 枚举（与后端 GowdDocumentTypeEnum / GowdIndividualDocumentTypeEnum /
 *    GowdRequiredDocumentSet / GowdUserExtParamVo 对齐） ──
 *
 * attachments[].fileType 用的是「平台统一枚举」ChannelFileDocumentType，不是渠道原文：
 * GowdParamCheckHelper.parseFileType 明确拒绝 socialContract 这类渠道原文
 * （"fileType 只认平台统一枚举"），渠道原文由中台 GowdDocumentTypeEnum.fromUnified
 * 在出站时转换。与文件上传接口的 documentType 同一套词表——上传时传 ID_CARD 拿回
 * fileId，这里继续传 ID_CARD。 */

// 平台统一类型 → 渠道原文，仅用于页面提示：渠道回传的 reprovedFields 用的是原文，
// QA 对照缺件时需要这张表。企业与个人的渠道原文不同（Representative vs Holder）。
const GOWD_CHANNEL_VALUE_BUSINESS = {
  ID_CARD: 'documentRepresentative', PASSPORT: 'documentRepresentative',
  DRIVER_LICENSE: 'documentRepresentative', ID_CARD_FRONT: 'documentRepresentative',
  ID_CARD_BACK: 'documentRepresentative', NATIONAL_IDENTITY: 'documentRepresentative',
  SELFIE: 'selfieRepresentative', COMPANY_PROOF: 'socialContract', COMPANY_TAX_ID: 'cnpjCard',
  ADDRESS_PROOF: 'proofAddress', BANK_ADDRESS_PROOF: 'proofBankAddress',
  PARTNER_ADDRESS_PROOF: 'partnerProofAddress', TAX_BILL: 'ir', KYC_QUESTIONNAIRE: 'kyc',
  PEP_DECLARATION: 'pepDeclaration', INCOME_STATEMENT: 'incomeStatement', BALANCE_SHEET: 'balanceSheet',
};
const GOWD_CHANNEL_VALUE_INDIVIDUAL = {
  ID_CARD: 'documentHolder', PASSPORT: 'documentHolder', DRIVER_LICENSE: 'documentHolder',
  ID_CARD_FRONT: 'documentHolder', ID_CARD_BACK: 'documentHolder', NATIONAL_IDENTITY: 'documentHolder',
  SELFIE: 'selfieHolder', ACCOUNT_TERMS: 'accountTerms', PEP_DECLARATION: 'pepDeclaration',
};
// 可选类型清单 = 各自 supportedUnifiedTypes()。个人主体支持的类型比企业少得多，
// 且多出 ACCOUNT_TERMS；给个人传 COMPANY_PROOF 这类企业专有类型会被同步拒单。
const GOWD_DOC_TYPES_BUSINESS = Object.keys(GOWD_CHANNEL_VALUE_BUSINESS);
const GOWD_DOC_TYPES_INDIVIDUAL = Object.keys(GOWD_CHANNEL_VALUE_INDIVIDUAL);
const gowdDocTypes = (userType) => (userType === 'INDIVIDUAL' ? GOWD_DOC_TYPES_INDIVIDUAL : GOWD_DOC_TYPES_BUSINESS);
const gowdChannelValue = (userType, fileType) => (userType === 'INDIVIDUAL'
  ? GOWD_CHANNEL_VALUE_INDIVIDUAL : GOWD_CHANNEL_VALUE_BUSINESS)[fileType];

// 必传清单：缺一类中台同步拒单，不会到渠道。口径是平台统一类型。
// 企业主体当前「所有附件都非必输」——清单实际由
// fiat_channel_account.extra_config.organizationRequiredDocuments 控制（配空串＝不强制），
// 代码里的默认值 GowdRequiredDocumentSet.BUSINESS 被该配置覆盖。
// 个人主体仍保留 3 类（individualRequiredDocuments 未放开）。
const GOWD_REQUIRED_DOCS = {
  BUSINESS: [],
  INDIVIDUAL: ['ID_CARD', 'SELFIE', 'PEP_DECLARATION'],
};
// 预填清单：不强制不代表不该传——渠道审核是人工的，材料不全会挂在 PENDING_ANALYSE 数天。
// 所以企业仍按渠道 2026-09-20 邮件确认的 5 类预填，只是不再标 ★、也不拦提交。
const GOWD_SUGGESTED_DOCS = {
  BUSINESS: ['COMPANY_PROOF', 'ID_CARD', 'SELFIE', 'BALANCE_SHEET', 'PEP_DECLARATION'],
  INDIVIDUAL: ['ID_CARD', 'SELFIE', 'PEP_DECLARATION'],
};
// 巴西 27 个联邦单位（州二字码），address.state 必须是其中之一
const GOWD_BR_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];
// 附件行的默认值：按主体类型预置必传类型，避免漏项
const gowdDefaultDocs = (userType) => (GOWD_SUGGESTED_DOCS[userType] || GOWD_SUGGESTED_DOCS.BUSINESS)
  .map(ft => ({ fileType: ft, fileId: '', fileName: '' }));

const CURRENCY_LIST = [
  'USD','EUR','GBP','SGD','HKD','JPY','AUD','CAD','CHF','CNY',
  'INR','MYR','THB','PHP','IDR','VND','KRW','TWD','NZD','SEK',
  'NOK','DKK','PLN','CZK','HUF','RON','BGN','HRK','TRY','ZAR',
  'BRL','MXN','COP','CLP','PEN','ARS','AED','SAR','QAR','KWD',
  'BHD','OMR','JOD','EGP','NGN','KES','GHS','TZS','UGX',
].map(c => ({ label: c, value: c }));

const sectionStyle = { fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #f0f0f0' };
const sectionStyleMt = { ...sectionStyle, marginTop: 8 };
const gridCol2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };
const gridCol3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 };
const gridCol4 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 };

/* ── Address sub-form ── */
function AddressFields({ prefix, label, required = true, t }) {
  return (
    <div style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 16px', marginBottom: 12, border: '1px solid #e5e7eb' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>📍 {label}{!required && ` (${t('optional')})`}</div>
      <div style={gridCol3}>
        <Form.Item name={[...prefix, 'country']} label={t('tzAddressCountry')} rules={[{ required }]}><Input placeholder="SG" /></Form.Item>
        <Form.Item name={[...prefix, 'state']} label={t('tzAddressState')} rules={[{ required }]}><Input placeholder="Singapore" /></Form.Item>
        <Form.Item name={[...prefix, 'city']} label={t('tzAddressCity')} rules={[{ required }]}><Input placeholder="Singapore" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name={[...prefix, 'postcode']} label={t('tzAddressPostcode')} rules={[{ required }]}><Input placeholder="123456" /></Form.Item>
        <Form.Item name={[...prefix, 'line1']} label={t('tzAddressLine1')} rules={[{ required }]}><Input placeholder="1 Test Street" /></Form.Item>
      </div>
    </div>
  );
}

/* ── Build Tazapay extParam JSON from form values ── */
function buildTazapayExtParam(values) {
  const ext = {};
  if (values.tz_email) ext.email = values.tz_email;
  if (values.tz_description) ext.description = values.tz_description;
  if (values.tz_phone) ext.phone = values.tz_phone;
  if (values.tz_phoneCode) ext.phoneCode = values.tz_phoneCode;
  if (values.tz_tax) ext.tax = values.tz_tax;
  if (values.tz_website) ext.website = values.tz_website;
  ext.industry = values.tz_industry;
  if (values.tz_tp_value || values.tz_tp_volume) {
    ext.transactionProfile = {
      monthlyExpectedTransactionsValue: values.tz_tp_value,
      monthlyExpectedTransactionVolume: values.tz_tp_volume,
      expectedTransactionCountries: values.tz_tp_countries ? values.tz_tp_countries.split(',').map(s => s.trim()) : undefined,
      expectedTransactionCurrencies: values.tz_tp_currencies ? values.tz_tp_currencies.split(',').map(s => s.trim()) : undefined,
      customerRisk: values.tz_tp_risk,
    };
  }
  ext.kybInfo = {
    companyType: values.tz_companyType, identifyNo: values.tz_identifyNo,
    companyName: values.tz_companyName,
    registerAddress: values.tz_registerAddress, operationAddress: values.tz_operationAddress,
    attachments: (values.tz_companyDocs || []).map(d => ({ fileType: d.fileType, subType: d.subType, tag: d.tag, name: d.name, describe: d.describe, fileId: d.fileId })),
    legalInfo: (values.tz_legalInfo || []).map(l => {
      const legal = {
        name: l.name, nationality: l.nationality,
        role: l.role, ownershipPercentage: l.ownershipPercentage,
        attachments: (l.attachments || []).map(d => ({ fileType: d.fileType, subType: d.subType, tag: d.tag, name: d.name, describe: d.describe, fileId: d.fileId })),
      };
      if (l.lastName) legal.lastName = l.lastName;
      if (l.dateOfBirth) legal.dateOfBirth = typeof l.dateOfBirth === 'string' ? l.dateOfBirth : l.dateOfBirth.format('YYYY-MM-DD');
      if (l.phone) legal.phone = l.phone;
      if (l.phoneCode) legal.phoneCode = l.phoneCode;
      if (l.residentialAddress?.country) legal.residentialAddress = l.residentialAddress;
      return legal;
    }),
  };
  if (values.tz_establishDate) ext.kybInfo.establishDate = typeof values.tz_establishDate === 'string' ? values.tz_establishDate : values.tz_establishDate.format('YYYY-MM-DD');
  if (values.tz_statementDescribe) ext.kybInfo.statementDescribe = values.tz_statementDescribe;
  return JSON.stringify(ext);
}

/* ── Build MDAQ (KYB) extParam JSON from form values ── */
function buildMdaqExtParam(values) {
  const ext = {
    email: values.mdaq_email,
    firstName: values.mdaq_firstName,
    lastName: values.mdaq_lastName,
    countryCode: values.mdaq_countryCode,
  };
  if (values.mdaq_language) ext.language = values.mdaq_language;

  const company = {
    companyName: values.mdaq_companyName,
    businessType: values.mdaq_businessType,
    registrationNumber: values.mdaq_registrationNumber,
    companyAddress: values.mdaq_companyAddress,
    postalCode: values.mdaq_postalCode,
    city: values.mdaq_city,
    state: values.mdaq_state,
    countryOfIncorporation: values.mdaq_countryOfIncorporation,
    countryOfOperations: values.mdaq_countryOfOperations,
  };
  if (values.mdaq_incorporationDate) {
    company.incorporationDate = typeof values.mdaq_incorporationDate === 'string'
      ? values.mdaq_incorporationDate
      : values.mdaq_incorporationDate.format('DD-MM-YYYY');
  }
  if (values.mdaq_sourceOfFunds) company.sourceOfFunds = values.mdaq_sourceOfFunds;
  ext.companyInfo = company;

  // cddInfo 全部可选，任一有值才组装
  const cdd = {};
  const cddMap = {
    customerBusinessWebsite: values.mdaq_cdd_website,
    customerBusinessDescription: values.mdaq_cdd_description,
    customerAcquisitionChannel: values.mdaq_cdd_acquisitionChannel,
    entityType: values.mdaq_cdd_entityType,
    ownershipStructureLayers: values.mdaq_cdd_ownershipLayers,
    paymentModes: values.mdaq_cdd_paymentModes,
    onboardingMode: values.mdaq_cdd_onboardingMode,
    productServiceComplexity: values.mdaq_cdd_productComplexity,
    topCounterPartiesInbound: values.mdaq_cdd_inbound,
    topCounterPartiesOutbound: values.mdaq_cdd_outbound,
    sourceOfFunds: values.mdaq_cdd_sourceOfFunds,
    purposeOfAccountOpening: values.mdaq_cdd_purpose,
  };
  Object.entries(cddMap).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') cdd[k] = v; });
  if (values.mdaq_cdd_anticipatedInbound !== undefined && values.mdaq_cdd_anticipatedInbound !== null) {
    cdd.anticipatedValueMonthlyTransactionsInbound = values.mdaq_cdd_anticipatedInbound;
  }
  if (Object.keys(cdd).length > 0) ext.cddInfo = cdd;

  return JSON.stringify(ext);
}

/* ── Build TransFi (individual user) extParam JSON from form values ── */
function buildTransfiExtParam(values) {
  const ext = {
    firstName: values.tf_firstName,
    lastName: values.tf_lastName,
    date: typeof values.tf_date === 'string'
      ? values.tf_date
      : (values.tf_date ? values.tf_date.format('DD-MM-YYYY') : undefined),
    email: values.tf_email,
    phone: values.tf_phone,
    phoneCode: values.tf_phoneCode,
    country: values.tf_country,
  };
  if (values.tf_countryOfResidence) ext.countryOfResidence = values.tf_countryOfResidence;
  if (values.tf_gender) ext.gender = values.tf_gender;
  // sumsubToken 必填（后端 @NotBlank）：创建用户成功后据此导入 Shared KYC，一次性、用后失效
  if (values.tf_sumsubToken) ext.sumsubToken = values.tf_sumsubToken;

  // address 子对象：country 必须与顶层 country 一致（实测约束）
  const address = {};
  if (values.tf_street) address.street = values.tf_street;
  if (values.tf_city) address.city = values.tf_city;
  if (values.tf_state) address.state = values.tf_state;
  if (values.tf_postalCode) address.postalCode = values.tf_postalCode;
  if (values.tf_country) address.country = values.tf_country;
  if (Object.keys(address).length > 0) ext.address = address;

  return JSON.stringify(ext);
}

/* ── Build MOOLAHGO (KYC) extParam JSON from form values ── */
function buildMoolahgoExtParam(values) {
  const ext = {
    nameTitle: values.mg_nameTitle,
    nameFirst: values.mg_nameFirst,
    nameLast: values.mg_nameLast,
    contactPhone: values.mg_contactPhone,
    contactEmail: values.mg_contactEmail,
    contactAddress: values.mg_contactAddress,
    contactCity: values.mg_contactCity,
    contactCountryOfResidence: values.mg_contactCountryOfResidence,
    contactPostalCode: values.mg_contactPostalCode,
    icType: values.mg_icType,
    icNumber: values.mg_icNumber,
    icCountry: values.mg_icCountry,
    personNationality: values.mg_personNationality,
    personDob: values.mg_personDob,
    personGender: values.mg_personGender,
    personOccupation: values.mg_personOccupation,
  };
  // 去掉空值
  Object.keys(ext).forEach(k => { if (ext[k] === undefined || ext[k] === null || ext[k] === '') delete ext[k]; });
  return JSON.stringify(ext);
}

/* ── Build AVENIA (KYB) extParam JSON from form values ──
 * 结构与后端 AveniaUserExtParamVo 对齐：companyInfo(含 address / attachments) + uboList + sandboxReject。
 * 企业地址在后端提交渠道时会映射为带 company 前缀的字段（companyStreetLine1 等），此处按中台入参命名。 */
function buildAveniaExtParam(values) {
  const address = {
    streetLine1: values.av_streetLine1,
    city: values.av_city,
    state: values.av_state,
    zipCode: values.av_zipCode,
    country: values.av_country,
  };
  const companyInfo = {
    companyName: values.av_companyName,
    // 渠道要求法定名称全大写，前端直接规范化，避免因大小写被平台前置拦截
    companyLegalName: values.av_companyLegalName ? values.av_companyLegalName.toUpperCase() : undefined,
    companyRegistrationNumber: values.av_companyRegistrationNumber,
    taxIdentificationNumberTin: values.av_taxIdentificationNumberTin,
    businessActivityDescription: values.av_businessActivityDescription,
    reasonForAccountOpening: values.av_reasonForAccountOpening,
    sourceOfFundsAndIncome: values.av_sourceOfFundsAndIncome,
    numberOfEmployees: values.av_numberOfEmployees,
    estimatedAnnualRevenueUsd: values.av_estimatedAnnualRevenueUsd,
    estimatedMonthlyVolumeUsd: values.av_estimatedMonthlyVolumeUsd,
    countryTaxResidence: values.av_countryTaxResidence,
    countrySubdivisionTaxResidence: values.av_countrySubdivisionTaxResidence,
    website: values.av_website,
    socialMedia: values.av_socialMedia,
    emailPixKey: values.av_emailPixKey,
    address,
    attachments: (values.av_companyDocs || [])
      .filter(d => d && d.fileType && d.fileId)
      .map(d => ({
        fileType: d.fileType,
        fileId: d.fileId,
        fileName: d.fileName || undefined,
        // 企业类文档只能单面
        isDoubleSided: false,
      })),
  };
  Object.keys(companyInfo).forEach(k => {
    const v = companyInfo[k];
    if (v === undefined || v === null || v === '') delete companyInfo[k];
  });

  const uboList = (values.av_uboList || []).map(u => {
    const uboAddress = {
      streetLine1: u.streetLine1,
      city: u.city,
      state: u.state,
      zipCode: u.zipCode,
      country: u.country,
    };
    const ubo = {
      fullName: u.fullName,
      dateOfBirth: typeof u.dateOfBirth === 'string'
        ? u.dateOfBirth
        : (u.dateOfBirth ? u.dateOfBirth.format('YYYY-MM-DD') : undefined),
      countryOfTaxId: u.countryOfTaxId,
      taxIdNumber: u.taxIdNumber,
      percentageOfOwnership: u.percentageOfOwnership === undefined || u.percentageOfOwnership === null
        ? undefined
        : String(u.percentageOfOwnership),
      hasControl: u.hasControl,
      documentCountry: u.documentCountry,
      email: u.email,
      phone: u.phone,
      address: uboAddress,
      attachments: (u.attachments || [])
        .filter(d => d && d.fileType && d.fileId)
        .map(d => {
          const doc = { fileType: d.fileType, fileId: d.fileId, fileName: d.fileName || undefined };
          // 仅身份证件类允许双面；双面时背面文件必填
          if (AVENIA_IDENTITY_DOC_TYPES.includes(d.fileType) && d.isDoubleSided) {
            doc.isDoubleSided = true;
            doc.backFileId = d.backFileId;
            doc.backFileName = d.backFileName || undefined;
          } else {
            doc.isDoubleSided = false;
          }
          return doc;
        }),
    };
    Object.keys(ubo).forEach(k => {
      const v = ubo[k];
      if (v === undefined || v === null || v === '') delete ubo[k];
    });
    return ubo;
  });

  const ext = { companyInfo, uboList };
  // sandboxReject 仅用于沙箱模拟拒绝；生产环境后端不会透传给渠道
  if (values.av_sandboxReject === 'true') ext.sandboxReject = true;
  return JSON.stringify(ext);
}

/* ── Build GOWD extParam JSON from form values ──
 * 结构与后端 GowdUserExtParamVo 对齐：
 *   顶层 country/fullName/email/phone/documentType/documentNumber/address
 *   + companyInfo{establishDate, legalRepresentatives[]}（仅企业）
 *   + individualInfo{dateOfBirth}（仅个人）
 *   + attachments[]
 * 主体类型不进 extParam：走公共 userType（BUSINESS/INDIVIDUAL），中台内部转渠道的 ORGANIZATION/INDIVIDUAL。
 * documentType 与主体日期语义都随 userType 变化，故由 userType 推导而非让用户各填一次。
 *
 * 注意三处与渠道原文的差异，中台只认左边这套：
 *   documentType/documentNumber 在顶层（不是嵌套 document{type,number}）
 *   成立日期叫 companyInfo.establishDate、出生日期叫 individualInfo.dateOfBirth（不是顶层 birthdate）
 *   代表人叫 companyInfo.legalRepresentatives、其生日叫 dateOfBirth（不是 representatives/birthdate）
 * 填错容器会被 checkSubjectInfo 同步拒单（个人传 companyInfo 直接 400，不是静默忽略）。 */
function buildGowdExtParam(values) {
  const isBiz = values.userType === 'BUSINESS';
  const toDate = (v) => (typeof v === 'string' ? v : (v ? v.format('YYYY-MM-DD') : undefined));
  const ext = {
    country: values.gw_country,
    fullName: values.gw_fullName,
    email: values.gw_email,
    phone: values.gw_phone,
    // 企业固定 CNPJ、个人固定 CPF，由主体类型决定，避免两者填反导致渠道校验位报错
    documentType: isBiz ? 'CNPJ' : 'CPF',
    documentNumber: values.gw_documentNumber,
    address: {
      street: values.gw_street,
      neighborhood: values.gw_neighborhood,
      city: values.gw_city,
      state: values.gw_state,
      zipCode: values.gw_zipCode,
      complement: values.gw_complement || undefined,
    },
    attachments: (values.gw_attachments || [])
      .filter(d => d && d.fileType && d.fileId)
      .map(d => ({ fileType: d.fileType, fileId: d.fileId, fileName: d.fileName || undefined })),
  };
  // 主体差异进各自的容器：企业只能带 companyInfo、个人只能带 individualInfo，
  // 带错容器中台直接 400（"individualInfo must not be provided for userType=BUSINESS"）
  if (isBiz) {
    ext.companyInfo = {
      establishDate: toDate(values.gw_birthdate),
      legalRepresentatives: (values.gw_representatives || [])
        .filter(r => r && r.fullName)
        .map(r => ({
          fullName: r.fullName,
          documentNumber: r.documentNumber,
          country: r.country,
          email: r.email,
          phone: r.phone,
          dateOfBirth: toDate(r.birthdate),
        })),
    };
  } else {
    ext.individualInfo = { dateOfBirth: toDate(values.gw_birthdate) };
  }
  Object.keys(ext.address).forEach(k => { if (!ext.address[k]) delete ext.address[k]; });
  Object.keys(ext).forEach(k => {
    const v = ext[k];
    if (v === undefined || v === null || v === '') delete ext[k];
  });
  return JSON.stringify(ext);
}

export default function UserCreate() {
  const { t } = useLanguage();
  const [createForm] = Form.useForm();
  const [queryForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [queryResult, setQueryResult] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  const [selectedCreateChannel, setSelectedCreateChannel] = useState(null);
  const isTazapay = selectedCreateChannel === 'TAZAPAY';
  const isMdaq = selectedCreateChannel === 'MDAQ';
  const isTransfi = selectedCreateChannel === 'TRANSFI';
  const isMoolahgo = selectedCreateChannel === 'MOOLAHGO';
  const isAvenia = selectedCreateChannel === 'AVENIA';
  const isGowd = selectedCreateChannel === 'GOWD';

  // GOWD 的 document.type、birthdate 语义、代表人是否必填、必传文件清单都随主体类型变化，
  // 表单需要跟着 userType 实时重绘，所以这里 watch 而不是只在渠道切换时取一次。
  const gowdUserType = Form.useWatch('userType', createForm) || 'BUSINESS';
  const gowdIsBiz = gowdUserType === 'BUSINESS';

  /**
   * 一键填入 GOWD 示例值（沙箱可直接提交通过）。
   *
   * 不是"模板 JSON"，而是把示例值写进各个表单字段——校验规则仍然生效，
   * 便于在示例基础上改单个字段做边界测试。
   */
  const fillGowdSample = () => {
    const stamp = Date.now().toString().slice(-6);
    const isBiz = gowdIsBiz;
    createForm.setFieldsValue({
      currency: 'BRL',
      gw_country: 'BRA',
      gw_fullName: isBiz ? `OSL PAY TEST ${stamp} LTDA` : `TEST USER ${stamp}`,
      gw_email: `test.${stamp}@example.com`,
      gw_phone: '+5511999990001',
      // 企业填成立日期，个人填出生日期
      gw_birthdate: isBiz ? '2020-01-15' : '1990-01-01',
      gw_documentNumber: isBiz ? '62845739000151' : '39053344705',
      gw_street: 'Av Paulista 1636',
      gw_neighborhood: 'Bela Vista',
      gw_city: 'Sao Paulo',
      gw_state: 'SP',
      gw_zipCode: '01310100',
      gw_complement: '',
      // 附件地址故意留空：占位地址（example.com 之类）会让开户同步返回 200、
      // 却在异步下载阶段永久失败（FAILED: attachment download failed），
      // 比同步报错更难排查。所以强制人工填真实可下载地址。
      gw_attachments: gowdDefaultDocs(gowdUserType),
      gw_representatives: isBiz ? [{
        fullName: `REP ${stamp}`,
        documentNumber: '39053344705',
        country: 'BRA',
        email: `rep.${stamp}@example.com`,
        phone: '+5511999990002',
        birthdate: '1990-01-01',
      }] : [],
    });
  };
  const [, forceUpdate] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const loadUploadedFiles = async () => {
    const uid = localStorage.getItem('uid') || localStorage.getItem('username');
    if (!uid) return;
    try {
      const { data } = await metaApi.getUploadedFiles(uid, 'TAZAPAY');
      const list = data?.data || [];
      setUploadedFiles(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Failed to load uploaded files:', e);
    }
  };

  const getFileIdOptionsByType = (docType) => {
    const files = (docType ? uploadedFiles.filter(f => f.documentType === docType) : uploadedFiles)
      .filter(f => f.s3Exists !== false);
    return files.map(f => {
      const fid = f.fileId ? f.fileId.slice(-6) : '';
      let timeStr = '';
      if (f.createdAt) {
        const d = new Date(typeof f.createdAt === 'number' ? f.createdAt : Date.parse(f.createdAt));
        timeStr = isNaN(d.getTime()) ? '' : d.toLocaleString();
      }
      return {
        label: `📄 ${f.originalFileName || f.fileId} [${fid}]${timeStr ? ` ${timeStr}` : ''}`,
        value: f.localFilePath || f.storageObjectKey || f.fileId,
        originalFileName: f.originalFileName || f.fileId,
      };
    });
  };

  const handleCreateChannelChange = (ch) => {
    setSelectedCreateChannel(ch);
    // 仅在外部用户ID 为空时预填登录名作为默认值，避免覆盖用户已手动输入的值
    const uid = localStorage.getItem('uid') || localStorage.getItem('username') || '';
    if (!createForm.getFieldValue('externalUserId')) {
      createForm.setFieldsValue({ externalUserId: uid });
    }
    if (ch === 'TAZAPAY') {
      createForm.setFieldsValue({ userType: 'BUSINESS' });
      loadUploadedFiles();
    }
    if (ch === 'MDAQ') {
      // MDAQ 本期仅 KYB（BUSINESS），币种默认 SGD
      createForm.setFieldsValue({ userType: 'BUSINESS', currency: 'SGD' });
    }
    if (ch === 'TRANSFI') {
      // TransFi onramp/offramp 面向个人用户（INDIVIDUAL），使用专门表单字段
      createForm.setFieldsValue({ userType: 'INDIVIDUAL' });
    }
    if (ch === 'MOOLAHGO') {
      // MOOLAHGO KYC 仅支持 INDIVIDUAL
      createForm.setFieldsValue({ userType: 'INDIVIDUAL', currency: 'HKD' });
    }
    if (ch === 'AVENIA') {
      // Avenia 本期仅 KYB（BUSINESS），币种固定 BRL
      createForm.setFieldsValue({ userType: 'BUSINESS', currency: 'BRL' });
    }
    if (ch === 'CORPAY') {
      createForm.setFieldsValue({ userType: 'BUSINESS' });
    }
    if (ch === 'GOWD') {
      // GOWD 固定 BRL；个人开户受渠道账户 individualEnabled 开关控制（当前关闭），故默认 BUSINESS
      createForm.setFieldsValue({
        userType: 'BUSINESS', currency: 'BRL', gw_country: 'BRA', gw_state: 'SP',
        gw_attachments: gowdDefaultDocs('BUSINESS'),
        gw_representatives: [{ country: 'BRA' }],
      });
    }
  };

  useEffect(() => { testApi.getChannelCurrencies?.().catch(() => {}); }, []);

  // GOWD 企业/个人的必传清单不同（5 类 vs 3 类），主体类型切换后沿用旧行会直接撞上缺件拒单，
  // 所以跟着重置附件行。仅在切换时触发，首次选中渠道已由 handleCreateChannelChange 预置。
  const gowdPrevUserType = useRef(null);
  useEffect(() => {
    if (!isGowd) { gowdPrevUserType.current = null; return; }
    if (gowdPrevUserType.current && gowdPrevUserType.current !== gowdUserType) {
      createForm.setFieldsValue({
        gw_attachments: gowdDefaultDocs(gowdUserType),
        gw_representatives: gowdUserType === 'BUSINESS' ? [{ country: 'BRA' }] : [],
      });
    }
    gowdPrevUserType.current = gowdUserType;
  }, [isGowd, gowdUserType, createForm]);

  const handleCreate = async (values) => {
    setLoading(true);
    try {
      let extParam = values.extParam || undefined;
      if (isTazapay) extParam = buildTazapayExtParam(values);
      if (isMdaq) extParam = buildMdaqExtParam(values);
      if (isTransfi) extParam = buildTransfiExtParam(values);
      if (isMoolahgo) extParam = buildMoolahgoExtParam(values);
      if (isAvenia) extParam = buildAveniaExtParam(values);
      if (isGowd) extParam = buildGowdExtParam(values);
      const requestBody = {
        requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        channelCode: values.channelCode, externalUserId: values.externalUserId,
        userType: values.userType, currency: values.currency || undefined,
        operateType: values.operateType || 'CREATE', extParam,
      };
      const username = localStorage.getItem('username') || 'admin';
      const { data } = await testApi.executeTest({
        testType: 'USER_CREATE', channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'test',
        currency: values.currency || '', env: 'SANDBOX', executor: username,
        requestBody: JSON.stringify(requestBody),
      });
      const res = data;
      if (res.data?.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch {}
      }
      setCreateResult(res);
      if (res.data?.success) message.success(t('userCreateSubmitted'));
      else message.warning(`${t('userCreateFailed')}: ${res.data?.errorMessage || res.msg || t('unknownError')}`);
    } catch (error) {
      message.error(t('userCreateFailed') + ': ' + (error.response?.data?.msg || error.message));
      setCreateResult(error.response?.data || { error: error.message });
    } finally { setLoading(false); }
  };

  const handleQuery = async (values) => {
    setQueryLoading(true);
    try {
      // channelCode 需随 requestBody 传给后端（查询类走 GET，requestBody 转成 query 参数）；
      // 后端按 externalUserId 查询时不传 channelCode 会默认 PYVIO，导致 TRANSFI 等其它渠道查不到。
      const requestBody = { requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, externalUserId: values.externalUserId || undefined, userId: values.userId || undefined, channelCode: values.channelCode || undefined };
      const { data } = await testApi.executeTest({ testType: 'USER_QUERY', channelCode: values.channelCode || '', businessPartner: values.businessPartner || 'test', currency: '', env: 'SANDBOX', executor: localStorage.getItem('username') || 'admin', requestBody: JSON.stringify(requestBody) });
      const res = data;
      if (res.data?.responseBody && typeof res.data.responseBody === 'string') { try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch {} }
      setQueryResult(res);
    } catch (error) { message.error(t('userQueryFailed') + ': ' + (error.response?.data?.msg || error.message)); setQueryResult(error.response?.data || { error: error.message }); }
    finally { setQueryLoading(false); }
  };

  const handleReset = () => { createForm.resetFields(); queryForm.resetFields(); setCreateResult(null); setQueryResult(null); setSelectedCreateChannel(null); };

  /* ── Tazapay KYB form fields ── */
  const renderTazapayFields = () => (
    <>
      <div style={sectionStyleMt}>🏢 {t('tzCompanyBasicInfo')}</div>
      <div style={gridCol2}>
        <Form.Item name="tz_industry" label={t('tzIndustry')} rules={[{ required: true }]} tooltip={t('tzIndustryRequired')}><Select showSearch optionFilterProp="label" placeholder="选择行业" options={TAZAPAY_INDUSTRY_OPTIONS} /></Form.Item>
        <Form.Item name="tz_email" label={t('email')}><Input placeholder="company@example.com" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="tz_phone" label={t('tzPhoneLabel')}><Input placeholder="1234567890" /></Form.Item>
        <Form.Item name="tz_phoneCode" label={t('tzPhoneCode')}><Input placeholder="65" /></Form.Item>
        <Form.Item name="tz_tax" label={t('tzTaxId')}><Input placeholder="T12345678X" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="tz_website" label={t('tzWebsite')}><Input placeholder="https://example.com" /></Form.Item>
        <Form.Item name="tz_description" label={t('tzBusinessDesc')}><Input placeholder="Test company description" /></Form.Item>
      </div>

      <div style={sectionStyleMt}>📋 {t('tzKybCompanyInfo')}</div>
      <div style={gridCol3}>
        <Form.Item name="tz_companyType" label={t('tzCompanyTypeLabel')} rules={[{ required: true }]}><Select options={TAZAPAY_COMPANY_TYPES.map(v => ({ label: v, value: v }))} /></Form.Item>
        <Form.Item name="tz_companyName" label={t('tzCompanyNameLabel')} rules={[{ required: true }]}><Input placeholder="Test Company Ltd" /></Form.Item>
        <Form.Item name="tz_identifyNo" label={t('tzRegNo')} rules={[{ required: true }]}><Input placeholder="REG123456" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="tz_establishDate" label={t('tzEstablishDate')}><DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={t('tzSelectDate')} /></Form.Item>
        <Form.Item name="tz_statementDescribe" label={t('tzStatementDesc')}><Input placeholder={t('optional')} /></Form.Item>
      </div>

      {/* 注册/运营地址按业务要求「校验位必填」：还原为 required=true（仅 line2 在子表单内保持可选） */}
      <AddressFields prefix={['tz_registerAddress']} label={t('tzRegisterAddress')} required={true} t={t} />
      <AddressFields prefix={['tz_operationAddress']} label={t('tzOperationAddress')} required={true} t={t} />

      <div style={sectionStyleMt}>📎 {t('tzCompanyDocs')}</div>
      <Form.List name="tz_companyDocs" initialValue={[{ fileType: 'business', subType: 'other', tag: 'registrationProofDoc', name: '', describe: '', fileId: '' }]}>
        {(fields, { add, remove }) => (<>
          {fields.map(({ key, name, ...rest }) => (
            <div key={key} style={{ background: '#f0f5ff', borderRadius: 8, padding: '12px 16px', marginBottom: 10, border: '1px solid #d6e4ff', position: 'relative' }}>
              {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', position: 'absolute', top: 12, right: 12, cursor: 'pointer' }} />}
              <div style={gridCol2}>
                <Form.Item {...rest} name={[name, 'fileType']} label={t('tzFileType')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                  <Select placeholder={t('tzFileType')} options={TAZAPAY_DOC_TYPES_COMPANY} />
                </Form.Item>
                <Form.Item {...rest} name={[name, 'fileId']} label={t('tzLinkedFile')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                  <Select placeholder={t('tzSelectUploadedFile')} showSearch optionFilterProp="label" options={getFileIdOptionsByType()}
                    onChange={(val) => {
                      const file = uploadedFiles.find(f => (f.localFilePath || f.storageObjectKey || f.fileId) === val);
                      if (file) createForm.setFieldValue(['tz_companyDocs', name, 'name'], file.originalFileName || file.fileId);
                    }} />
                </Form.Item>
              </div>
              {/* subType（→ 渠道 sub_type，必填）与 tag（→ 渠道 tag，必填，枚举） */}
              <div style={gridCol2}>
                <Form.Item {...rest} name={[name, 'subType']} label={t('tzFileSubType')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                  <Select placeholder={t('tzFileSubType')} options={TAZAPAY_DOC_SUBTYPES} />
                </Form.Item>
                <Form.Item {...rest} name={[name, 'tag']} label={t('tzFileTag')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                  <Select placeholder={t('tzFileTag')} options={TAZAPAY_DOC_TAGS_COMPANY} />
                </Form.Item>
              </div>
              <div style={gridCol2}>
                <Form.Item {...rest} name={[name, 'name']} label={t('tzFileName')} rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="registration.pdf" /></Form.Item>
                <Form.Item {...rest} name={[name, 'describe']} label={t('tzFileDesc')} rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="Company registration" /></Form.Item>
              </div>
            </div>
          ))}
          <Button type="dashed" onClick={() => add({ fileType: 'business', subType: 'other', tag: 'additionalDocs' })} icon={<PlusOutlined />} block style={{ marginBottom: 12 }}>{t('tzAddDoc')}</Button>
        </>)}
      </Form.List>

      <div style={sectionStyleMt}>👤 {t('tzLegalInfo')}</div>
      <Form.List name="tz_legalInfo" initialValue={[{ name: '', lastName: '', nationality: '', role: ['director'], ownershipPercentage: 100, dateOfBirth: '', phone: '', phoneCode: '', attachments: [{ fileType: 'identity', subType: 'passport', tag: 'additionalDocs', name: '', describe: '', fileId: '' }] }]}>
        {(fields, { add, remove }) => (<>
          {fields.map(({ key, name, ...rest }) => (
            <div key={key} style={{ background: '#fefce8', borderRadius: 8, padding: '12px 16px', marginBottom: 12, border: '1px solid #fde68a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Tag color="orange">{t('tzRepresentative')} #{name + 1}</Tag>
                {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', cursor: 'pointer' }} />}
              </div>
              <div style={gridCol4}>
                <Form.Item {...rest} name={[name, 'name']} label={t('tzFirstName')} rules={[{ required: true }]}><Input /></Form.Item>
                <Form.Item {...rest} name={[name, 'lastName']} label={t('tzLastName')}><Input /></Form.Item>
                <Form.Item {...rest} name={[name, 'nationality']} label={t('tzNationality')} rules={[{ required: true }]}><Input placeholder="SG" /></Form.Item>
                <Form.Item {...rest} name={[name, 'dateOfBirth']} label={t('tzDob')} rules={[{ required: true, message: t('tzSelectDob') }]}><DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={t('tzSelectDate')} /></Form.Item>
              </div>
              <div style={gridCol4}>
                <Form.Item {...rest} name={[name, 'role']} label={t('tzRole')} rules={[{ required: true }]}><Select mode="multiple" options={TAZAPAY_LEGAL_ROLES.map(v => ({ label: v, value: v }))} /></Form.Item>
                <Form.Item {...rest} name={[name, 'ownershipPercentage']} label={t('tzOwnership')} rules={[({ getFieldValue }) => ({
                  validator(_, value) {
                    const roles = getFieldValue(['tz_legalInfo', name, 'role']) || [];
                    const need = roles.includes('shareholder') || roles.includes('beneficial_owner');
                    if (need && (value === undefined || value === null || value === '')) {
                      return Promise.reject(new Error('持股相关角色(shareholder/beneficial_owner)必填持股比例'));
                    }
                    return Promise.resolve();
                  }
                })]}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item>
                <Form.Item {...rest} name={[name, 'phone']} label={t('tzPhoneLabel')}><Input /></Form.Item>
                <Form.Item {...rest} name={[name, 'phoneCode']} label={t('tzPhoneCode')}><Input placeholder="65" /></Form.Item>
              </div>
              <Form.Item {...rest} name={[name, 'residentialAddress']} noStyle><Input type="hidden" /></Form.Item>
              <AddressFields prefix={['tz_legalInfo', name, 'residentialAddress']} label={t('tzResidentialAddress')} required={false} t={t} />
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>📎 {t('tzRepDocs')}</div>
              <Form.List name={[name, 'attachments']}>
                {(docFields, docOps) => (<>
                  {docFields.map(({ key: dk, name: dn, ...dr }) => (
                    <div key={dk} style={{ background: '#fff', borderRadius: 6, padding: '10px 12px', marginBottom: 8, border: '1px solid #e5e7eb', position: 'relative' }}>
                      {docFields.length > 1 && <MinusCircleOutlined onClick={() => docOps.remove(dn)} style={{ color: '#ff4d4f', position: 'absolute', top: 10, right: 10, cursor: 'pointer' }} />}
                      <div style={gridCol2}>
                        <Form.Item {...dr} name={[dn, 'fileType']} label={t('tzFileType')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                          <Select placeholder={t('tzFileType')} options={TAZAPAY_DOC_TYPES_LEGAL} />
                        </Form.Item>
                        <Form.Item {...dr} name={[dn, 'fileId']} label={t('tzLinkedFile')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                          <Select placeholder={t('tzSelectUploadedFile')} showSearch optionFilterProp="label" options={getFileIdOptionsByType()}
                            onChange={(val) => {
                              const file = uploadedFiles.find(f => (f.localFilePath || f.storageObjectKey || f.fileId) === val);
                              if (file) createForm.setFieldValue(['tz_legalInfo', name, 'attachments', dn, 'name'], file.originalFileName || file.fileId);
                            }} />
                        </Form.Item>
                      </div>
                      {/* subType（→ 渠道 sub_type，必填）与 tag（→ 渠道 tag，必填，枚举） */}
                      <div style={gridCol2}>
                        <Form.Item {...dr} name={[dn, 'subType']} label={t('tzFileSubType')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                          <Select placeholder={t('tzFileSubType')} options={TAZAPAY_DOC_SUBTYPES} />
                        </Form.Item>
                        <Form.Item {...dr} name={[dn, 'tag']} label={t('tzFileTag')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                          <Select placeholder={t('tzFileTag')} options={TAZAPAY_DOC_TAGS_LEGAL} />
                        </Form.Item>
                      </div>
                      <div style={gridCol2}>
                        <Form.Item {...dr} name={[dn, 'name']} label={t('tzFileName')} rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="passport.pdf" /></Form.Item>
                        <Form.Item {...dr} name={[dn, 'describe']} label={t('tzFileDesc')} rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="Passport" /></Form.Item>
                      </div>
                    </div>
                  ))}
                  <Button type="dashed" size="small" onClick={() => docOps.add({ fileType: 'identity', subType: 'passport', tag: 'additionalDocs' })} icon={<PlusOutlined />} block>{t('tzAddRepDoc')}</Button>
                </>)}
              </Form.List>
            </div>
          ))}
          <Button type="dashed" onClick={() => add({ role: ['director'], ownershipPercentage: 0, attachments: [{ fileType: 'identity', subType: 'passport', tag: 'additionalDocs' }] })} icon={<PlusOutlined />} block style={{ marginBottom: 12 }}>{t('tzAddRepresentative')}</Button>
        </>)}
      </Form.List>

      <div style={sectionStyleMt}>📊 {t('tzTransactionProfile')}</div>
      <div style={gridCol4}>
        <Form.Item name="tz_tp_value" label={t('tzMonthlyValue')}><InputNumber style={{ width: '100%' }} placeholder="10000" /></Form.Item>
        <Form.Item name="tz_tp_volume" label={t('tzMonthlyVolume')}><InputNumber style={{ width: '100%' }} placeholder="100" /></Form.Item>
        <Form.Item name="tz_tp_countries" label={t('tzCountries')}><Input placeholder="SG,US" /></Form.Item>
        <Form.Item name="tz_tp_risk" label={t('tzRiskLevel')}><Select allowClear options={[{ label: 'low', value: 'low' }, { label: 'medium', value: 'medium' }, { label: 'high', value: 'high' }]} /></Form.Item>
      </div>
    </>
  );

  /* ── MDAQ KYB form fields ── */
  const renderMdaqFields = () => (
    <>
      <div style={sectionStyleMt}>🏢 申请人基础信息</div>
      <div style={gridCol2}>
        <Form.Item name="mdaq_email" label="邮箱 (email)" rules={[{ required: true }]}><Input placeholder="company@example.com" /></Form.Item>
        <Form.Item name="mdaq_countryCode" label="国家码 (countryCode)" rules={[{ required: true }]} tooltip="alpha-2, 2字符"><Input placeholder="SG" maxLength={2} /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_firstName" label="名 (firstName)" rules={[{ required: true }]}><Input placeholder="Skyline Tech" /></Form.Item>
        <Form.Item name="mdaq_lastName" label="姓 (lastName)" rules={[{ required: true }]}><Input placeholder="Pte Ltd" /></Form.Item>
        <Form.Item name="mdaq_language" label="语言 (language)" tooltip="默认 en"><Input placeholder="en" /></Form.Item>
      </div>

      <div style={sectionStyleMt}>📋 公司信息 (companyInfo)</div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_companyName" label="公司名称 (companyName)" rules={[{ required: true }]}><Input placeholder="Skyline Tech Pte. Ltd." /></Form.Item>
        <Form.Item name="mdaq_businessType" label="业务类型 (businessType)" rules={[{ required: true }]}>
          <Select showSearch placeholder="选择业务类型" options={MDAQ_BUSINESS_TYPES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_registrationNumber" label="注册号 (registrationNumber)" rules={[{ required: true }]}><Input placeholder="202312345A" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_incorporationDate" label="注册日期 (incorporationDate)" rules={[{ required: true }]} tooltip="格式 dd-MM-yyyy">
          <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="13-01-2000" />
        </Form.Item>
        <Form.Item name="mdaq_countryOfIncorporation" label="注册国 (countryOfIncorporation)" rules={[{ required: true }]} tooltip="alpha-2"><Input placeholder="SG" maxLength={2} /></Form.Item>
        <Form.Item name="mdaq_countryOfOperations" label="经营国 (countryOfOperations)" rules={[{ required: true }]} tooltip="alpha-2"><Input placeholder="SG" maxLength={2} /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="mdaq_companyAddress" label="公司地址 (companyAddress)" rules={[{ required: true }]}><Input placeholder="1 Raffles Place" /></Form.Item>
        <Form.Item name="mdaq_postalCode" label="邮编 (postalCode)" rules={[{ required: true }]}><Input placeholder="048616" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_city" label="城市 (city)" rules={[{ required: true }]}><Input placeholder="Singapore" /></Form.Item>
        <Form.Item name="mdaq_state" label="州/省 (state)" rules={[{ required: true }]}><Input placeholder="Singapore" /></Form.Item>
        <Form.Item name="mdaq_sourceOfFunds" label="资金来源 (sourceOfFunds)">
          <Select allowClear options={MDAQ_SOURCE_OF_FUNDS.map(v => ({ label: v, value: v }))} />
        </Form.Item>
      </div>

      <div style={sectionStyleMt}>🔍 CDD 信息 (cddInfo，全部可选)</div>
      <div style={gridCol2}>
        <Form.Item name="mdaq_cdd_website" label="业务网站 (customerBusinessWebsite)"><Input placeholder="https://skylinetech.com" /></Form.Item>
        <Form.Item name="mdaq_cdd_description" label="业务描述 (customerBusinessDescription)"><Input placeholder="Software development" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="mdaq_cdd_acquisitionChannel" label="获客渠道 (customerAcquisitionChannel)"><Input placeholder="Merchant of YourCompany" /></Form.Item>
        <Form.Item name="mdaq_cdd_entityType" label="实体类型 (entityType)">
          <Select allowClear options={MDAQ_ENTITY_TYPES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_cdd_ownershipLayers" label="股权层级 (ownershipStructureLayers)">
          <Select allowClear options={MDAQ_OWNERSHIP_LAYERS.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_cdd_paymentModes" label="支付方式 (paymentModes)">
          <Select allowClear options={MDAQ_PAYMENT_MODES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_cdd_onboardingMode" label="开户方式 (onboardingMode)">
          <Select allowClear options={MDAQ_ONBOARDING_MODES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_cdd_productComplexity" label="产品复杂度 (productServiceComplexity)">
          <Select allowClear options={MDAQ_PRODUCT_COMPLEXITY.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_cdd_inbound" label="入款对手方 (topCounterPartiesInbound)" tooltip="逗号分隔"><Input placeholder="SG,US,HK" /></Form.Item>
        <Form.Item name="mdaq_cdd_outbound" label="出款对手方 (topCounterPartiesOutbound)" tooltip="逗号分隔"><Input placeholder="SG,US,HK" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_cdd_anticipatedInbound" label="月预计入款 (anticipatedValueMonthlyTransactionsInbound)"><InputNumber style={{ width: '100%' }} placeholder="100000" /></Form.Item>
        <Form.Item name="mdaq_cdd_sourceOfFunds" label="资金来源 (sourceOfFunds)">
          <Select allowClear options={MDAQ_SOURCE_OF_FUNDS.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_cdd_purpose" label="开户目的 (purposeOfAccountOpening)"><Input placeholder="Business collections" /></Form.Item>
      </div>
    </>
  );

  /* ── TransFi (individual user) form fields ── */
  const renderTransfiFields = () => (
    <>
      <div style={sectionStyleMt}>👤 个人基础信息（TransFi individual user）</div>
      <div style={gridCol3}>
        <Form.Item name="tf_firstName" label="名 (firstName)" rules={[{ required: true }]}><Input placeholder="John" /></Form.Item>
        <Form.Item name="tf_lastName" label="姓 (lastName)" rules={[{ required: true }]}><Input placeholder="Doe" /></Form.Item>
        <Form.Item name="tf_date" label="出生日期 (date)" rules={[{ required: true }]} tooltip="必填，格式 DD-MM-YYYY">
          <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="25-12-1985" />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="tf_email" label="邮箱 (email)" rules={[{ required: true, type: 'email' }]} tooltip="必填，邮箱格式。渠道幂等键之一(email 优先)：email 命中已存在用户则返回老用户。邮箱风控：自造邮箱可能 409 EMAIL_VERIFICATION_FAILED"><Input placeholder="john.doe@example.com" /></Form.Item>
        <Form.Item name="tf_phoneCode" label="国际区号 (phoneCode)" rules={[{ required: true, pattern: /^\+\d{1,4}$/, message: '格式如 +1 / +63' }]} tooltip="必填，格式 +区号，如 +63"><Input placeholder="+63" /></Form.Item>
        <Form.Item name="tf_phone" label="手机号 (phone)" rules={[{ required: true, pattern: /^\d{4,15}$/, message: '4-15 位数字' }]} tooltip="必填，4-15 位数字。渠道幂等键之一：phone 命中已存在用户则返回老用户。需同时换新 email+phone 才会在渠道新建用户"><Input placeholder="9171234567" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="tf_country" label="国籍 (country)" rules={[{ required: true }]} tooltip="ISO 2 位国家码，需与地址国家一致，否则地址校验失败"><Input placeholder="PH" maxLength={2} /></Form.Item>
        <Form.Item name="tf_countryOfResidence" label="居住国 (countryOfResidence)" tooltip="ISO 2 位国家码，可选"><Input placeholder="PH" maxLength={2} /></Form.Item>
        <Form.Item name="tf_gender" label="性别 (gender)">
          <Select allowClear options={[{ label: 'male', value: 'male' }, { label: 'female', value: 'female' }]} />
        </Form.Item>
      </div>

      <div style={sectionStyleMt}>🏠 地址信息（address 整块必填，country 自动取上方国籍，须与国籍一致）</div>
      <div style={gridCol2}>
        <Form.Item name="tf_street" label="街道 (street)" rules={[{ required: true }]}><Input placeholder="123 Rizal Street" /></Form.Item>
        <Form.Item name="tf_city" label="城市 (city)" rules={[{ required: true }]}><Input placeholder="Manila" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="tf_state" label="州/省 (state)" rules={[{ required: true }]}><Input placeholder="Metro Manila" /></Form.Item>
        <Form.Item name="tf_postalCode" label="邮编 (postalCode)" rules={[{ required: true }]}><Input placeholder="1000" /></Form.Item>
      </div>

      <div style={sectionStyleMt}>🔐 KYC 认证（Shared KYC token，创建成功后导入）</div>
      <Form.Item name="tf_sumsubToken" label="Sumsub 共享KYC token (sumsubToken)" rules={[{ required: true }]}
        tooltip="必填，由调用方提供。创建用户成功后据此导入 Shared KYC 复用已认证结果。token 一次性，用过即失效，每次需新生成；缺失或为空平台前置校验直接拦截">
        <TextArea rows={3} placeholder="_act-sbx-jwt-..." />
      </Form.Item>
    </>
  );

  /* ── MOOLAHGO KYC form fields ── */
  const renderMoolahgoFields = () => (
    <>
      <div style={sectionStyleMt}>👤 KYC 个人信息</div>
      <div style={gridCol3}>
        <Form.Item name="mg_nameTitle" label="称谓 (nameTitle)" rules={[{ required: true }]}>
          <Select options={['Mr', 'Ms', 'Mrs', 'Mdm', 'Dr'].map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mg_nameFirst" label="名 (nameFirst)" rules={[{ required: true }]}>
          <Input placeholder="John" />
        </Form.Item>
        <Form.Item name="mg_nameLast" label="姓 (nameLast)" rules={[{ required: true }]}>
          <Input placeholder="Doe" />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mg_personGender" label="性别 (personGender)" rules={[{ required: true }]}>
          <Select options={[{ label: 'M (男)', value: 'M' }, { label: 'F (女)', value: 'F' }, { label: 'U (未知)', value: 'U' }]} />
        </Form.Item>
        <Form.Item name="mg_personNationality" label="国籍 (personNationality)" rules={[{ required: true }]} tooltip="ISO 3166-1 alpha-2">
          <Input placeholder="SG" maxLength={2} style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item name="mg_personDob" label="出生日期 (personDob)" rules={[{ required: true }]} tooltip="YYYY-MM-DD">
          <Input placeholder="1990-01-01" />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mg_personOccupation" label="职业代码 (personOccupation)" rules={[{ required: true }]} tooltip="Appendix G: B01~B22">
          <Input placeholder="B01" />
        </Form.Item>
        <Form.Item name="mg_icType" label="证件类型 (icType)" rules={[{ required: true }]}>
          <Select options={[{ label: 'NIC (身份证)', value: 'NIC' }, { label: 'PP (护照)', value: 'PP' }, { label: 'WEP (工作准证)', value: 'WEP' }]} />
        </Form.Item>
        <Form.Item name="mg_icNumber" label="证件号 (icNumber)" rules={[{ required: true }]}>
          <Input placeholder="12345678" />
        </Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="mg_icCountry" label="证件签发国 (icCountry)" rules={[{ required: true }]} tooltip="ISO 3166-1 alpha-2">
          <Input placeholder="SG" maxLength={2} style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item name="mg_contactPhone" label="联系电话 (contactPhone)" rules={[{ required: true }]} tooltip="E.164格式，需唯一">
          <Input placeholder="+6591234567" />
        </Form.Item>
      </div>

      <div style={sectionStyleMt}>📍 联系地址</div>
      <div style={gridCol2}>
        <Form.Item name="mg_contactEmail" label="邮箱 (contactEmail)" rules={[{ required: true }]}>
          <Input placeholder="test@qq.com" />
        </Form.Item>
        <Form.Item name="mg_contactAddress" label="地址 (contactAddress)" rules={[{ required: true }]}>
          <Input placeholder="1 Raffles Place" />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mg_contactCity" label="城市 (contactCity)" rules={[{ required: true }]}>
          <Input placeholder="Singapore" />
        </Form.Item>
        <Form.Item name="mg_contactCountryOfResidence" label="居住国 (contactCountryOfResidence)" rules={[{ required: true }]} tooltip="ISO 3166-1 alpha-2">
          <Input placeholder="SG" maxLength={2} style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item name="mg_contactPostalCode" label="邮编 (contactPostalCode)" rules={[{ required: true }]}>
          <Input placeholder="048616" maxLength={16} />
        </Form.Item>
      </div>
    </>
  );

  /* ── AVENIA KYB form fields ──
   * 单次提交带齐「公司信息 + 全部 UBO + 全部文件」，中台内部依次完成
   * subaccount → documents(预签名直传) → ubos → KYB Level 1(API 模式)。
   * 审核结果纯 Webhook 驱动（KYC 订阅，levelName=kyb-level-1），无主动轮询。 */
  const renderAveniaFields = () => (
    <>
      <div style={sectionStyleMt}>🏢 公司信息 (companyInfo)</div>
      <div style={gridCol3}>
        <Form.Item name="av_companyName" label="公司名称 (companyName)" rules={[{ required: true }, { max: 64, message: '≤64 字符' }]} tooltip="作为 Avenia subaccount 的 name，≤64 字符，超长直接拒绝">
          <Input placeholder="OSL Pay Brasil" maxLength={64} />
        </Form.Item>
        <Form.Item name="av_companyLegalName" label="法定名称 (companyLegalName)" rules={[{ required: true }]} tooltip="渠道要求全大写提交；此处输入会自动转大写">
          <Input placeholder="OSL PAY BRASIL LTDA" style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item name="av_companyRegistrationNumber" label="企业注册号 (companyRegistrationNumber)" rules={[{ required: true }]}>
          <Input placeholder="40261549000154" />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="av_taxIdentificationNumberTin" label="企业税号 CNPJ (taxIdentificationNumberTin)" rules={[{ required: true }]} tooltip="巴西企业须为 14 位 CNPJ">
          <Input placeholder="40261549000154" />
        </Form.Item>
        <Form.Item name="av_countryTaxResidence" label="税务居民国 (countryTaxResidence)" rules={[{ required: true }]} tooltip="ISO alpha-3；无税务居民国可传 N/A">
          <Input placeholder="BRA" />
        </Form.Item>
        <Form.Item name="av_countrySubdivisionTaxResidence" label="税务居民国行政区 (countrySubdivisionTaxResidence)">
          <Input placeholder="SP" />
        </Form.Item>
      </div>
      <Form.Item name="av_businessActivityDescription" label="业务活动描述 (businessActivityDescription)" rules={[{ required: true }, { max: 2000, message: '≤2000 字符' }]} tooltip="1~2000 字符">
        <TextArea rows={2} placeholder="Cross-border payment services for institutional clients" />
      </Form.Item>
      <div style={gridCol2}>
        <Form.Item name="av_reasonForAccountOpening" label="开户原因 (reasonForAccountOpening)" rules={[{ required: true }]} tooltip="须按商户真实业务选择，不得对所有商户固定同一值">
          <Select showSearch options={AVENIA_REASON_FOR_ACCOUNT_OPENING.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="av_sourceOfFundsAndIncome" label="资金与收入来源 (sourceOfFundsAndIncome)" rules={[{ required: true }]}>
          <Select showSearch options={AVENIA_SOURCE_OF_FUNDS.map(v => ({ label: v, value: v }))} />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="av_numberOfEmployees" label="员工人数 (numberOfEmployees)" rules={[{ required: true }]}>
          <Select options={AVENIA_NUMBER_OF_EMPLOYEES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="av_estimatedAnnualRevenueUsd" label="预估年营收 (estimatedAnnualRevenueUsd)" rules={[{ required: true }]}>
          <Select options={AVENIA_ANNUAL_REVENUE_USD.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="av_estimatedMonthlyVolumeUsd" label="预估月交易额USD (estimatedMonthlyVolumeUsd)" rules={[{ required: true }]} tooltip="整数字符串">
          <Input placeholder="500000" />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="av_website" label="官网 (website)"><Input placeholder="https://oslpay.example.com" /></Form.Item>
        <Form.Item name="av_socialMedia" label="社交媒体 (socialMedia)"><Input placeholder="@oslpay" /></Form.Item>
        <Form.Item name="av_emailPixKey" label="Email 类型 PIX Key (emailPixKey)"><Input placeholder="pix@oslpay.example.com" /></Form.Item>
      </div>

      <div style={sectionStyleMt}>🏠 企业地址（提交渠道时映射为 companyStreetLine1 等带前缀字段）</div>
      <div style={gridCol2}>
        <Form.Item name="av_streetLine1" label="街道 (streetLine1)" rules={[{ required: true }]}><Input placeholder="Av. Paulista 1000" /></Form.Item>
        <Form.Item name="av_city" label="城市 (city)" rules={[{ required: true }]}><Input placeholder="Sao Paulo" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="av_state" label="州/省 (state)" rules={[{ required: true }]}><Input placeholder="SP" /></Form.Item>
        <Form.Item name="av_zipCode" label="邮编 (zipCode)" rules={[{ required: true }]}><Input placeholder="01310-100" /></Form.Item>
        <Form.Item name="av_country" label="国家 (country)" rules={[{ required: true }]} tooltip="ISO alpha-3"><Input placeholder="BRA" /></Form.Item>
      </div>

      <div style={sectionStyleMt}>📄 企业文档（最小集：注册证明 + 税号文件，各 1 份，只能单面）</div>
      <Form.List name="av_companyDocs" initialValue={AVENIA_COMPANY_DOC_TYPES.map(ft => ({ fileType: ft, fileId: '', fileName: '' }))}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...rest }) => (
              <div key={key} style={{ ...gridCol3, alignItems: 'end', marginBottom: 8 }}>
                <Form.Item {...rest} name={[name, 'fileType']} label="文档类型 (fileType)" rules={[{ required: true }]}>
                  <Select options={AVENIA_COMPANY_DOC_TYPES.map(v => ({ label: v, value: v }))} />
                </Form.Item>
                <Form.Item {...rest} name={[name, 'fileId']} label="文件地址 (fileId)" rules={[{ required: true }]} tooltip="业务方可下载的文件 URL，中台会下载后经预签名地址直传渠道">
                  <Input placeholder="https://files.example.com/certificate.pdf" />
                </Form.Item>
                <Space>
                  <Form.Item {...rest} name={[name, 'fileName']} label="文件名 (fileName)" tooltip="用于推断 Content-Type" style={{ flex: 1, marginBottom: 0 }}>
                    <Input placeholder="certificate_of_incorporation.pdf" />
                  </Form.Item>
                  {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ef4444' }} />}
                </Space>
              </div>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ fileType: AVENIA_COMPANY_DOC_TYPES[0], fileId: '', fileName: '' })} block>添加企业文档</Button>
          </>
        )}
      </Form.List>

      <div style={sectionStyleMt}>👥 UBO 列表（≤50，至少一个携带合法 hasControl 角色，年龄须 ≥18）</div>
      <Form.List name="av_uboList" initialValue={[{ fullName: '', dateOfBirth: '', countryOfTaxId: 'BRA', taxIdNumber: '', percentageOfOwnership: 100, hasControl: 'CEO', documentCountry: 'BRA', email: '', phone: '', streetLine1: '', city: '', state: '', zipCode: '', country: 'BRA', attachments: [{ fileType: 'ID', fileId: '', fileName: '', isDoubleSided: false }] }]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...rest }) => (
              <div key={key} style={{ border: '1px dashed #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Tag color="blue">UBO #{name + 1}</Tag>
                  {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ef4444' }} />}
                </div>
                <div style={gridCol3}>
                  <Form.Item {...rest} name={[name, 'fullName']} label="全名 (fullName)" rules={[{ required: true }]}><Input placeholder="Joao Silva" /></Form.Item>
                  <Form.Item {...rest} name={[name, 'dateOfBirth']} label="出生日期 (dateOfBirth)" rules={[{ required: true }]} tooltip="格式 yyyy-MM-dd；年龄须 ≥18，未满 18 平台直接拒绝">
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="1985-03-15" />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'hasControl']} label="控制角色 (hasControl)" rules={[{ required: true }]} tooltip="至少一个 UBO 必须携带合法角色，否则 KYB 无法通过">
                    <Select showSearch options={AVENIA_UBO_CONTROL_ROLES.map(v => ({ label: v, value: v }))} />
                  </Form.Item>
                </div>
                <div style={gridCol4}>
                  <Form.Item {...rest} name={[name, 'countryOfTaxId']} label="税号国家 (countryOfTaxId)" rules={[{ required: true }]} tooltip="ISO alpha-3"><Input placeholder="BRA" /></Form.Item>
                  <Form.Item {...rest} name={[name, 'taxIdNumber']} label="税号 CPF (taxIdNumber)" rules={[{ required: true }]} tooltip="巴西 UBO 须为有效 CPF(11 位)"><Input placeholder="12345678909" /></Form.Item>
                  <Form.Item {...rest} name={[name, 'percentageOfOwnership']} label="持股比例 (percentageOfOwnership)" rules={[{ required: true }]}>
                    <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="100" />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'documentCountry']} label="证件签发国 (documentCountry)" rules={[{ required: true }]}><Input placeholder="BRA" /></Form.Item>
                </div>
                <div style={gridCol2}>
                  <Form.Item {...rest} name={[name, 'email']} label="邮箱 (email)" rules={[{ required: true, type: 'email' }]}><Input placeholder="joao@example.com" /></Form.Item>
                  <Form.Item {...rest} name={[name, 'phone']} label="手机号 (phone)" tooltip="E.164 格式，如 +5511999887766"><Input placeholder="+5511999887766" /></Form.Item>
                </div>
                <div style={gridCol3}>
                  <Form.Item {...rest} name={[name, 'streetLine1']} label="街道 (streetLine1)" rules={[{ required: true }]}><Input placeholder="Rua Example 123" /></Form.Item>
                  <Form.Item {...rest} name={[name, 'city']} label="城市 (city)" rules={[{ required: true }]}><Input placeholder="Sao Paulo" /></Form.Item>
                  <Form.Item {...rest} name={[name, 'state']} label="州/省 (state)" rules={[{ required: true }]}><Input placeholder="SP" /></Form.Item>
                </div>
                <div style={gridCol2}>
                  <Form.Item {...rest} name={[name, 'zipCode']} label="邮编 (zipCode)" rules={[{ required: true }]}><Input placeholder="01310-100" /></Form.Item>
                  <Form.Item {...rest} name={[name, 'country']} label="国家 (country)" rules={[{ required: true }]} tooltip="ISO alpha-3"><Input placeholder="BRA" /></Form.Item>
                </div>

                <div style={{ fontSize: 12, color: '#64748b', margin: '4px 0 8px' }}>
                  📎 UBO 文档：至少 1 份身份证件（ID / DRIVERS-LICENSE / PASSPORT / RESIDENCE-PERMIT，可双面）；可选 SELFIE-FROM-LIVENESS 活体自拍（固定单面）
                </div>
                <Form.List {...rest} name={[name, 'attachments']}>
                  {(docFields, docOps) => (
                    <>
                      {docFields.map(({ key: dk, name: dn, ...dRest }) => (
                        <div key={dk} style={{ ...gridCol4, alignItems: 'end', marginBottom: 8 }}>
                          <Form.Item {...dRest} name={[dn, 'fileType']} label="文档类型" rules={[{ required: true }]}>
                            <Select options={AVENIA_UBO_DOC_TYPES.map(v => ({ label: v, value: v }))} />
                          </Form.Item>
                          <Form.Item {...dRest} name={[dn, 'fileId']} label="正面文件地址 (fileId)" rules={[{ required: true }]}>
                            <Input placeholder="https://files.example.com/ubo_id_front.png" />
                          </Form.Item>
                          <Form.Item {...dRest} name={[dn, 'isDoubleSided']} label="是否双面" tooltip="仅身份证件类允许 true；企业文档与活体自拍只能单面">
                            <Select options={[{ label: 'false (单面)', value: false }, { label: 'true (双面)', value: true }]} />
                          </Form.Item>
                          <Space>
                            <Form.Item {...dRest} name={[dn, 'backFileId']} label="背面文件地址 (backFileId)" tooltip="isDoubleSided=true 时必填" style={{ flex: 1, marginBottom: 0 }}>
                              <Input placeholder="https://files.example.com/ubo_id_back.png" />
                            </Form.Item>
                            {docFields.length > 1 && <MinusCircleOutlined onClick={() => docOps.remove(dn)} style={{ color: '#ef4444' }} />}
                          </Space>
                        </div>
                      ))}
                      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => docOps.add({ fileType: 'ID', fileId: '', fileName: '', isDoubleSided: false })} block>添加 UBO 文档</Button>
                    </>
                  )}
                </Form.List>
              </div>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ countryOfTaxId: 'BRA', documentCountry: 'BRA', country: 'BRA', percentageOfOwnership: 0, hasControl: 'Director', attachments: [{ fileType: 'ID', fileId: '', fileName: '', isDoubleSided: false }] })} block disabled={fields.length >= 50}>
              添加 UBO（最多 50 个）
            </Button>
          </>
        )}
      </Form.List>

      <div style={sectionStyleMt}>🧪 沙箱选项</div>
      <Form.Item name="av_sandboxReject" label="沙箱模拟拒绝 (sandboxReject)" tooltip="仅非生产环境透传给渠道用于模拟 KYB 拒绝；生产环境无论传什么都不发送该字段">
        <Select allowClear options={[{ label: 'false (正常审核)', value: 'false' }, { label: 'true (模拟拒绝)', value: 'true' }]} />
      </Form.Item>
    </>
  );

  /* ── GOWD 开户表单 ──
   * 单次提交带齐「主体信息 + 地址 + 代表人 + 全部必传文件」，中台内部依次完成
   * accounts 建档 → 逐份 documents 上传，审核结果由 Webhook 驱动（无轮询）。
   * 必填口径按 HML 实测（比渠道文档更严）：顶层 country/fullName/email/phone/
   * documentType/documentNumber/address 缺一即 400；企业还需 companyInfo.establishDate
   * 与至少一名 companyInfo.legalRepresentatives（每名 6 项齐全），个人还需
   * individualInfo.dateOfBirth。中台会在调渠道前先本地拦掉。 */
  const renderGowdFields = () => (
    <>
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button size="small" onClick={fillGowdSample}>填入示例值</Button>
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          当前主体：{gowdIsBiz ? '企业 (BUSINESS → 渠道 ORGANIZATION)' : '个人 (INDIVIDUAL)'}，
          证件类型自动取 {gowdIsBiz ? 'CNPJ' : 'CPF'}
        </span>
      </div>

      <div style={sectionStyleMt}>🏢 主体信息</div>
      <div style={gridCol3}>
        <Form.Item name="gw_fullName" label={gowdIsBiz ? '企业全称 (fullName)' : '个人姓名 (fullName)'} rules={[{ required: true }]}
          tooltip="企业填注册全称，个人填身份证件上的姓名">
          <Input placeholder={gowdIsBiz ? 'OSL PAY TEST LTDA' : 'JOAO DA SILVA'} />
        </Form.Item>
        <Form.Item name="gw_documentNumber" label={gowdIsBiz ? '企业税号 CNPJ (documentNumber)' : '个人税号 CPF (documentNumber)'} rules={[{ required: true }]}
          tooltip={gowdIsBiz ? '14 位纯数字，渠道会校验校验位' : '11 位纯数字，渠道会校验校验位'}>
          <Input placeholder={gowdIsBiz ? '62845739000151' : '39053344705'} />
        </Form.Item>
        <Form.Item name="gw_country" label="国家 (country)" rules={[{ required: true }]}
          tooltip="BR / BRA / Brazil 都接受，中台统一归一成渠道要求的 ISO3（BRA）">
          <Input placeholder="BRA" />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="gw_email" label="邮箱 (email)" rules={[{ required: true, type: 'email' }]}>
          <Input placeholder="contact@example.com" />
        </Form.Item>
        <Form.Item name="gw_phone" label="手机号 (phone)" rules={[{ required: true }]} tooltip="E.164 格式，须带 + 与国家码">
          <Input placeholder="+5511999990001" />
        </Form.Item>
        <Form.Item name="gw_birthdate" label={gowdIsBiz ? '成立日期 (companyInfo.establishDate)' : '出生日期 (individualInfo.dateOfBirth)'} rules={[{ required: true }]}
          tooltip="格式 yyyy-MM-dd。企业填 companyInfo.establishDate、个人填 individualInfo.dateOfBirth —— 渠道侧共用一个 birthdate 字段，该差异由中台内部消化，上游不要传顶层 birthdate">
          <Input placeholder={gowdIsBiz ? '2020-01-15' : '1990-01-01'} />
        </Form.Item>
      </div>

      <div style={sectionStyleMt}>🏠 地址 (address)</div>
      <div style={gridCol3}>
        <Form.Item name="gw_street" label="街道门牌 (street)" rules={[{ required: true }]}><Input placeholder="Av Paulista 1636" /></Form.Item>
        <Form.Item name="gw_neighborhood" label="街区 (neighborhood)" tooltip="巴西的 bairro。中台不强制（checkAddress 只必填 street/city/zipCode），是否必须交由渠道判定"><Input placeholder="Bela Vista" /></Form.Item>
        <Form.Item name="gw_city" label="城市 (city)" rules={[{ required: true }]}><Input placeholder="Sao Paulo" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="gw_state" label="州 (state)" tooltip="巴西州二字码。中台不强制，但传了就必须是两位字母">
          <Select showSearch options={GOWD_BR_STATES.map(v => ({ label: v, value: v }))} placeholder="SP" />
        </Form.Item>
        <Form.Item name="gw_zipCode" label="邮编 CEP (zipCode)" rules={[{ required: true }, { pattern: /^\d{8}$/, message: '8 位纯数字，不含连字符' }]}>
          <Input placeholder="01310100" maxLength={8} />
        </Form.Item>
        <Form.Item name="gw_complement" label="地址补充 (complement)" tooltip="选填，如楼层房号"><Input placeholder="Andar 10" /></Form.Item>
      </div>

      {gowdIsBiz && (
        <>
          <div style={sectionStyleMt}>👥 法定代表人 (companyInfo.legalRepresentatives)　企业主体必填，至少一名，六项齐全</div>
          <Form.List name="gw_representatives" initialValue={[{ country: 'BRA' }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <div key={key} style={{ border: '1px dashed #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Tag color="blue">代表人 #{name + 1}</Tag>
                      {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ef4444' }} />}
                    </div>
                    <div style={gridCol3}>
                      <Form.Item {...rest} name={[name, 'fullName']} label="姓名 (fullName)" rules={[{ required: true }]}><Input placeholder="JOAO DA SILVA" /></Form.Item>
                      <Form.Item {...rest} name={[name, 'documentNumber']} label="CPF (documentNumber)" rules={[{ required: true }]} tooltip="11 位纯数字，渠道校验校验位"><Input placeholder="39053344705" /></Form.Item>
                      <Form.Item {...rest} name={[name, 'country']} label="国家 (country)" rules={[{ required: true }]}><Input placeholder="BRA" /></Form.Item>
                    </div>
                    <div style={gridCol3}>
                      <Form.Item {...rest} name={[name, 'email']} label="邮箱 (email)" rules={[{ required: true, type: 'email' }]}><Input placeholder="rep@example.com" /></Form.Item>
                      <Form.Item {...rest} name={[name, 'phone']} label="手机号 (phone)" rules={[{ required: true }]} tooltip="E.164 格式"><Input placeholder="+5511999990002" /></Form.Item>
                      <Form.Item {...rest} name={[name, 'birthdate']} label="出生日期 (dateOfBirth)" rules={[{ required: true }]} tooltip="格式 yyyy-MM-dd；出站字段名为 companyInfo.legalRepresentatives[].dateOfBirth"><Input placeholder="1990-01-01" /></Form.Item>
                    </div>
                  </div>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ country: 'BRA' })} block>添加代表人</Button>
              </>
            )}
          </Form.List>
        </>
      )}

      <div style={sectionStyleMt}>
        📄 开户附件 (attachments)　{gowdIsBiz
          ? `企业当前不强制任何类型，建议仍带齐：${GOWD_SUGGESTED_DOCS.BUSINESS.join(' / ')}`
          : `个人须齐 3 类：${GOWD_REQUIRED_DOCS.INDIVIDUAL.join(' / ')}`}
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
        {gowdIsBiz
          ? '企业主体当前不强制附件（由渠道账户 extra_config.organizationRequiredDocuments 控制，已配成不强制），少传不会被拦；但渠道审核是人工的，材料不全会挂在 PENDING_ANALYSE 数天才反馈缺件。'
          : '个人主体缺任一必传类型会被中台同步拒单（本地不落库、渠道无申请记录），响应里会列出缺失项。'}
        多传其他类型无害，但<b>必须是本主体类型支持的类型</b>（个人主体传 COMPANY_PROOF 这类企业专有类型会被拒）。
        ID_CARD / SELFIE / PARTNER_ADDRESS_PROOF 对应的渠道类型是多主体类型，同类型可传多份（多名代表人各一套）。
        <b>fileType 填平台统一类型名（大写下划线）</b>，不要填渠道原文 socialContract —— 中台 parseFileType 会直接拒绝。
      </div>
      <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
        ⚠️ 文件地址支持两种形态，可混用：
        <div style={{ marginTop: 4 }}>
          <b>① 预上传文件号</b>（推荐）：先调文件上传接口拿 <code>fileId</code>（如 CF20260921GW00000039），
          文件名可留空（自动取上传时的文件名）。存在性与归属在下单即校验，且不受地址过期影响。
          注意上传时的 externalUserId 必须与本页一致，否则判越权。
        </div>
        <div style={{ marginTop: 4 }}>
          <b>② 可下载 URL</b>：必须是<b>服务端真能下载</b>的 http(s) 地址，且文件名必填。
          地址不可达时本接口<b>仍返回 200</b>，但开户会在异步下载阶段永久失败，
          要查询用户才看到 status=FAILED、failedReason 为 attachment download failed。
          用上传接口返回的预签名地址时注意有效期 7 天，过期会 403。
        </div>
      </div>
      <Form.List name="gw_attachments" initialValue={gowdDefaultDocs('BUSINESS')}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...rest }) => (
              <div key={key} style={{ ...gridCol3, alignItems: 'end', marginBottom: 8 }}>
                {/* 企业主体附件全部非必输，且前端一律不拦提交。
                    填了 fileId 却没选类型时只给橙色告警（warningOnly）不阻断 —— 这种行会被
                    buildGowdExtParam 的 (fileType && fileId) 过滤掉，提示一下免得以为传上去了，
                    但要不要这么提交由使用者自己决定。个人主体的 3 类是中台硬性必传，保持必填。 */}
                <Form.Item {...rest} name={[name, 'fileType']} label="文件类型 (fileType)"
                  dependencies={[['gw_attachments', name, 'fileId']]}
                  rules={[({ getFieldValue }) => ({
                    required: !gowdIsBiz || !!getFieldValue(['gw_attachments', name, 'fileId']),
                    warningOnly: gowdIsBiz,
                    message: '已填文件地址/文件号但未选类型，该行提交时会被剔除',
                  })]}
                  tooltip="平台统一类型 ChannelFileDocumentType，与文件上传接口的 documentType 同一套词表；括号内是中台转换后发给渠道的原文，渠道回传 reprovedFields 时用的是它">
                  <Select showSearch options={gowdDocTypes(gowdUserType).map(v => ({
                    label: `${v}${(GOWD_REQUIRED_DOCS[gowdUserType] || []).includes(v) ? ' ★必传' : ''} → ${gowdChannelValue(gowdUserType, v)}`,
                    value: v,
                  }))} />
                </Form.Item>
                <Form.Item {...rest} name={[name, 'fileId']} label="文件地址或文件号 (fileId)"
                  rules={[{ required: !gowdIsBiz }]}
                  tooltip="两种都支持：可下载的 http(s) URL，或文件上传接口返回的 fileId（如 CF20260921GW00000039）。企业主体可整行留空，留空的行提交时会被剔除">
                  <Input placeholder="https://... 或 CF20260921GW00000039" />
                </Form.Item>
                <Space>
                  <Form.Item {...rest} name={[name, 'fileName']} label="文件名 (fileName)"
                    tooltip="传 URL 时必填（URL 路径未必带正确扩展名，而扩展名参与格式判定）；传文件号时可留空，自动取上传时的原始文件名"
                    style={{ flex: 1, marginBottom: 0 }}>
                    <Input placeholder="传文件号可留空" />
                  </Form.Item>
                  {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ef4444' }} />}
                </Space>
              </div>
            ))}
            <Space style={{ marginTop: 4 }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ fileType: gowdDocTypes(gowdUserType)[0], fileId: '', fileName: '' })}>添加附件</Button>
              <Button size="small" onClick={() => createForm.setFieldsValue({ gw_attachments: gowdDefaultDocs(gowdUserType) })}>
                重置为{gowdIsBiz ? '企业建议' : '个人必传'}清单
              </Button>
            </Space>
          </>
        )}
      </Form.List>
    </>
  );

  /* ── Result panel ── */
  const renderResultPanel = (result, emptyIcon, emptyText) => (
    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 20, height: '100%', minHeight: 300 }}>
      {result ? (
        <ReactJson src={result} collapsed={1} theme="rjv-default" displayDataTypes={false} displayObjectSize={false} enableClipboard />
      ) : (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8, border: '2px dashed #e5e7eb' }}>
          {emptyIcon}
          <div style={{ marginTop: 12 }}>{emptyText}</div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1600, margin: '0 auto' }}>
      <div className="fade-in-up">
        {/* Page header — matching ChannelAccountManagement style */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space size={12}>
            <UserAddOutlined style={{ color: '#667eea', fontSize: 20 }} />
            <span style={{ fontSize: 20, fontWeight: 600 }}>{t('channelUserManagement')}</span>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>{t('reset')}</Button>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          /* ── Create Tab ── */
          { key: 'create', label: <span><UserAddOutlined /> {t('createUserTab')}</span>, children: (
            <div style={{ display: 'grid', gridTemplateColumns: (isTazapay || isMdaq || isTransfi || isMoolahgo || isAvenia || isGowd) ? '2fr 1fr' : '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 24 }}>
                <Form form={createForm} layout="vertical" onFinish={handleCreate} initialValues={{ userType: 'INDIVIDUAL', operateType: 'CREATE', businessPartner: 'test', externalUserId: localStorage.getItem('uid') || localStorage.getItem('username') || '' }}>
                  <Form.Item name="businessPartner" hidden><Input /></Form.Item>

                  <div style={sectionStyle}>📋 {t('basicInfo')}</div>
                  <div style={gridCol2}>
                    <Form.Item label={t('channelLabel')} name="channelCode" rules={[{ required: true, message: t('pleaseSelect', t('channelLabel')) }]}>
                      <Select placeholder={t('selectChannelPlaceholder')} onChange={handleCreateChannelChange}
                        options={CHANNELS.map(c => { const logo = getChannelLogo(c); return { label: <Space><span style={{ fontSize: 16 }}>{logo.emoji}</span><span>{logo.name}</span></Space>, value: c }; })} />
                    </Form.Item>
                    <Form.Item label={t('userTypeLabel')} name="userType" rules={[{ required: true }]}>
                      <Select options={[{ label: t('individualUser'), value: 'INDIVIDUAL' }, { label: t('businessUser'), value: 'BUSINESS' }]} />
                    </Form.Item>
                  </div>
                  <div style={gridCol2}>
                    <Form.Item label={t('externalUserIdLabel')} name="externalUserId" tooltip="需与文件上传时填写的外部用户ID保持一致，否则创建用户时捞不到已上传的文件" rules={[{ required: true, message: t('pleaseInputExternalUserId') }]}>
                      <Input style={{ fontFamily: 'monospace' }} placeholder="例如: mdaq_kyb_1780904457" />
                    </Form.Item>
                    <Form.Item label="操作类型 (operateType)" name="operateType" rules={[{ required: true }]}>
                      <Select options={[{ label: 'CREATE (创建)', value: 'CREATE' }, { label: 'UPDATE (更新)', value: 'UPDATE' }]} />
                    </Form.Item>
                  </div>
                  <div style={gridCol2}>
                    <Form.Item label={t('currencyLabel')} name="currency">
                      <Select placeholder={t('selectCurrencyPlaceholder')} showSearch allowClear options={CURRENCY_LIST} />
                    </Form.Item>
                  </div>

                  {isTazapay ? renderTazapayFields() : isMdaq ? renderMdaqFields() : isTransfi ? renderTransfiFields() : isMoolahgo ? renderMoolahgoFields() : isAvenia ? renderAveniaFields() : isGowd ? renderGowdFields() : (
                    <>
                      <div style={sectionStyleMt}>🔧 {t('extParamSection')}</div>
                      <Form.Item name="extParam" tooltip={t('extParamTooltip')}>
                        <TextArea rows={3} placeholder='{"key": "value"}' style={{ fontFamily: 'monospace', fontSize: 12 }} />
                      </Form.Item>
                    </>
                  )}

                  <Button type="primary" htmlType="submit" icon={<UserAddOutlined />} loading={loading} size="large" block style={{ height: 44, marginTop: 12 }}>
                    {loading ? t('creatingUser') : t('createUserBtn')}
                  </Button>
                </Form>
              </div>

              <div>
                <div style={sectionStyle}>📋 {t('createResultTitle')}</div>
                {renderResultPanel(createResult,
                  <UserAddOutlined style={{ fontSize: 40, color: '#d1d5db' }} />,
                  t('submitToShowCreateResult')
                )}
              </div>
            </div>
          )},

          /* ── Query Tab ── */
          { key: 'query', label: <span><SearchOutlined /> {t('queryUserTab')}</span>, children: (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 24 }}>
                <Form form={queryForm} layout="vertical" onFinish={handleQuery} initialValues={{ businessPartner: 'test', channelCode: 'TRANSFI' }}>
                  <Form.Item name="businessPartner" hidden><Input /></Form.Item>
                  <div style={sectionStyle}>🔍 {t('queryConditionsSection')}</div>
                  <Form.Item label={t('channel')} name="channelCode" tooltip="按 externalUserId 查询时必须指定渠道（同一 externalUserId 在不同渠道可能重复）；仅传 userId 时可不选">
                    <Select options={CHANNELS.map(c => ({ label: c, value: c }))} placeholder={t('selectChannel')} allowClear />
                  </Form.Item>
                  <Form.Item label={t('externalUserIdLabel')} name="externalUserId" tooltip={t('externalUserIdTooltip')}><Input placeholder="e.g. user_9988" /></Form.Item>
                  <Form.Item label={t('userIdLabel')} name="userId" tooltip={t('userIdTooltip')}><Input placeholder="e.g. UR20260418..." style={{ fontFamily: 'monospace' }} /></Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={queryLoading} size="large" block style={{ height: 44 }}>
                    {queryLoading ? t('queryingUser') : t('queryUserBtn')}
                  </Button>
                </Form>
              </div>

              <div>
                <div style={sectionStyle}>📋 {t('queryResultTitle')}</div>
                {renderResultPanel(queryResult,
                  <SearchOutlined style={{ fontSize: 40, color: '#d1d5db' }} />,
                  t('submitToShowQueryResult')
                )}
              </div>
            </div>
          )},
        ]} />
      </div>
    </div>
  );
}
